import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { displayAuthorName } from "@/lib/anonAuthor";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import FullscreenMedia from "@/components/FullscreenMedia";
import ManualBlurEditor from "@/components/ManualBlurEditor";
import { toast } from "sonner";
import { Shield, Users, FileWarning, CheckCircle2, XCircle, Ban, PauseCircle, PlayCircle, HandCoins, ScrollText, Eye, EyeOff, Trash2, RotateCcw, Expand, Palette, ChevronRight, Filter, Search, X, MoreHorizontal, Camera, AlertOctagon, MessageSquare, Download, Upload, Flag, AlertTriangle } from "lucide-react";

const CATS = {
  waste: ["small_pile","bags","plastic","glass","paper","organic","tires","appliances","furniture","construction","industrial","hazardous","asbestos","chemicals","oil","batteries","illegal_dump","marine","river","forest","sewage_spill","medical_waste","explosive_flammable"],
  environment: ["water_pollution","soil_pollution","suspicious_discharge","smoke_emissions","fire","fire_damage","damaged_trees","degraded_green","water_leak","erosion","contamination","beach_coast_degradation"],
  animals: ["injured","abandoned","trapped","in_danger","suspected_mistreatment","poor_conditions","wildlife_distress"],
  urban: ["broken_light","broken_bench","damaged_playground","pothole","architectural_barrier","dangerous_sidewalk","broken_signage","abandoned_public","vandalism","other"],
  vegetation: ["tall_grass","brambles","dangerous_branches","obstructing_visibility","fallen_tree","invasive_plants","road_encroachment","other"],
  reusable: ["gift","bed","mattress","table","chair","wardrobe","sofa","furniture","home_decor","appliance","electronics","computer","bicycle","tools","clothing","toys","construction_materials","books","other"],
};

const RISK_COLOR = { green: "#10B981", yellow: "#EAB308", red: "#EF4444" };

// Report status buckets exposed to the multi-select filter
const STATUS_BUCKETS = [
  { k: "in_progress", labelKey: "admin_st_in_progress", match: (r) => ["in_progress","in_review","reopened"].includes(r.status) },
  { k: "pending_approval", labelKey: "admin_st_pending_approval", match: (r) => r.status === "pending_moderation" },
  { k: "approved", labelKey: "admin_st_approved", match: (r) => ["published","resolved","confirmed"].includes(r.status) },
  { k: "archived", labelKey: "admin_st_archived", match: (r) => ["archived","hidden","rejected"].includes(r.status) },
];

const URGENT_BUCKETS = [
  { k: "pending_approval", labelKey: "admin_urgent_state_pending" },
  { k: "active", labelKey: "admin_urgent_state_active" },
  { k: "expired_to_verify", labelKey: "admin_urgent_state_expired" },
  { k: "ext_pending", labelKey: "admin_urgent_ext_pending" },
  { k: "archived", labelKey: "admin_urgent_state_archived" },
  { k: "rejected", labelKey: "admin_urgent_state_rejected" },
];

const STATUS_OPTIONS = [
  { v: "published", labelKey: "st_pub_visible" },
  { v: "in_progress", labelKey: "st_taken" },
  { v: "in_review", labelKey: "st_review" },
  { v: "standby", labelKey: "st_standby" },
  { v: "hidden", labelKey: "st_hidden" },
  { v: "archived", labelKey: "st_archived" },
  { v: "resolved", labelKey: "st_resolved" },
  { v: "reopened", labelKey: "st_reopened" },
  { v: "pending_moderation", labelKey: "st_pending" },
  { v: "rejected", labelKey: "st_rejected" },
];

// LocalStorage-persisted filter state helper
function usePersistentState(key, initial) {
  const [value, setValue] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : initial; } catch { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(value)); } catch {} }, [key, value]);
  return [value, setValue];
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [section, setSection] = usePersistentState("admin_section_v2", "home");
  const [colorFocus, setColorFocus] = usePersistentState("admin_color_focus", null); // "red" | "yellow" | "green" | null

  // Filters (all persisted for continuity after opening a detail)
  const [statusBuckets, setStatusBuckets] = usePersistentState("admin_status_buckets", []); // in_progress/pending_approval/approved/archived
  const [colorFilter, setColorFilter] = usePersistentState("admin_color_filter", []); // red/yellow/green
  const [searchQ, setSearchQ] = usePersistentState("admin_search", "");
  const [filterCategory, setFilterCategory] = usePersistentState("admin_filter_cat", "all");
  const [filterDate, setFilterDate] = usePersistentState("admin_filter_date", ""); // ISO YYYY-MM-DD
  const [filterAiConf, setFilterAiConf] = usePersistentState("admin_filter_ai_conf", 0); // percentage 0..100
  const [filterSensitive, setFilterSensitive] = usePersistentState("admin_filter_sensitive", false);
  const [filterLegal, setFilterLegal] = usePersistentState("admin_filter_legal", false);
  const [filtersOpen, setFiltersOpen] = usePersistentState("admin_filters_open", false);
  const [advOpen, setAdvOpen] = usePersistentState("admin_adv_open", false);

  // Photo review filters
  const [phStatus, setPhStatus] = usePersistentState("adm_ph_status", []); // pending/approved/rejected/archived
  const [phReason, setPhReason] = usePersistentState("adm_ph_reason", []);
  const [phSource, setPhSource] = usePersistentState("adm_ph_source", []);
  const [phAuthor, setPhAuthor] = usePersistentState("adm_ph_author", "");

  // Urgent filters
  const [urgentFilter, setUrgentFilter] = usePersistentState("adm_urg_state", []); // buckets

  // Data
  const [allReports, setAllReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [logs, setLogs] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [urgentAlerts, setUrgentAlerts] = useState([]);
  const [photosReview, setPhotosReview] = useState([]);
  const [userReports, setUserReports] = useState([]);
  const [urFilterStatus, setUrFilterStatus] = usePersistentState("adm_ur_status", []);
  const [urFilterReason, setUrFilterReason] = usePersistentState("adm_ur_reason", "all");
  // Content reports (iter60 legal compliance) — same admin/mod audience as
  // user-reports, but target is a piece of content (report / listing / message)
  // rather than a user profile.
  const [contentReports, setContentReports] = useState([]);
  const [crFilterStatus, setCrFilterStatus] = usePersistentState("adm_cr_status", []);
  const [crFilterReason, setCrFilterReason] = usePersistentState("adm_cr_reason", "all");

  // Extraordinary events (Lotto 6 #7) — admin/moderator management panel.
  const [events, setEvents] = useState([]);
  const [evFilterState, setEvFilterState] = usePersistentState("adm_ev_state", []);
  const [crFilterTarget, setCrFilterTarget] = usePersistentState("adm_cr_target", "all");
  const [showSensitive, setShowSensitive] = useState({});
  const [userSearch, setUserSearch] = useState(""); // legacy: not used after Lotto 13 refactor, kept only to avoid touching load()/reset flows.

  // Lotto 10 — Moderazione Admin: multi-select, stessa logica delle Verdi/Gialle/Rosse
  const [selectedPhotos, setSelectedPhotos] = useState(() => new Set());
  const [phBulkBusy, setPhBulkBusy] = useState(false);
  const photoKey = (p) => `${p.source}::${p.origin_id}::${p.photo_id}`;
  const togglePhotoSelected = (key) => setSelectedPhotos((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const clearPhotoSelection = () => setSelectedPhotos(new Set());

  // Lotto 11 — Marketplace Admin: 3 categorie (sale/gift/reusable),
  // stessa logica gestionale delle Verdi/Gialle/Rosse.
  const [listings, setListings] = useState([]);
  const [mkFilterType, setMkFilterType] = usePersistentState("adm_mk_type", "sale"); // sale|gift|reusable
  const [mkFilterState, setMkFilterState] = usePersistentState("adm_mk_state", []); // published|archived|closed|pending_confirmation
  const [mkSearchQ, setMkSearchQ] = usePersistentState("adm_mk_q", "");
  const [selectedListings, setSelectedListings] = useState(() => new Set());
  const [mkBulkBusy, setMkBulkBusy] = useState(false);
  const toggleListingSelected = (id) => setSelectedListings((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearListingSelection = () => setSelectedListings(new Set());
  const [listingDetail, setListingDetail] = useState(null); // {listing, checks}

  // ---- Bulk selection (Lotto 3 #7) ----
  // Selection is section-scoped (reset when navigating away from reports) and
  // held as a Set of report_id strings. Only rows currently visible in the
  // filtered list can be selected; a "Seleziona tutti" toggles the ids of the
  // visible slice.
  const [selectedReports, setSelectedReports] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleReportSelected = (id) => setSelectedReports((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearReportSelection = () => setSelectedReports(new Set());

  const toggleColor = (c) => setColorFilter((arr) => arr.includes(c) ? arr.filter((x) => x !== c) : [...arr, c]);
  const toggleBucket = (arr, k, setter) => setter(arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);

  const load = async () => {
    try {
      const [all, urg, photos, ev, mkList] = await Promise.all([
        api.get("/admin/reports"),
        api.get("/urgent-alerts", { params: { include_expired: true } }),
        api.get("/admin/photos-review").catch(() => ({ data: [] })),
        // Lotto 6 #7 — fetch ALL states (including pending_moderation) so
        // admins can approve/reject directly from the dashboard.
        api.get("/events", { params: { state: "pending_moderation", limit: 500 } }).catch(() => ({ data: [] })).then(async (pend) => {
          const pub = await api.get("/events", { params: { state: "published", limit: 500 } }).catch(() => ({ data: [] }));
          const conc = await api.get("/events", { params: { history: true, limit: 500 } }).catch(() => ({ data: [] }));
          return { data: [...(pend.data || []), ...(pub.data || []), ...(conc.data || [])] };
        }),
        // Lotto 11 — Marketplace admin fetch (all listings across all states)
        api.get("/admin/marketplace/listings", { params: { limit: 500 } }).catch(() => ({ data: [] })),
      ]);
      setAllReports(all.data || []);
      setUrgentAlerts(urg.data || []);
      setPhotosReview(photos.data || []);
      setEvents(ev.data || []);
      setListings(mkList.data || []);
      if (user?.role === "admin") {
        const [u, d, lg, fb, ur, cr] = await Promise.all([
          api.get("/admin/users"),
          api.get("/admin/donations").catch(() => ({ data: [] })),
          api.get("/admin/logs").catch(() => ({ data: [] })),
          api.get("/admin/feedback").catch(() => ({ data: [] })),
          api.get("/admin/user-reports").catch(() => ({ data: [] })),
          api.get("/admin/content-reports").catch(() => ({ data: { items: [] } })),
        ]);
        setUsers(u.data); setDonations(d.data); setLogs(lg.data); setFeedback(fb.data);
        setUserReports(ur.data || []);
        setContentReports((cr.data && cr.data.items) || []);
      } else if (user?.role === "moderator") {
        const [ur, cr] = await Promise.all([
          api.get("/admin/user-reports").catch(() => ({ data: [] })),
          api.get("/admin/content-reports").catch(() => ({ data: { items: [] } })),
        ]);
        setUserReports(ur.data || []);
        setContentReports((cr.data && cr.data.items) || []);
      }
    } catch (e) { toast.error(t("load_error")); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "moderator")) { nav("/"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, nav]);

  const decide = async (id, status, note) => {
    try { await api.patch(`/reports/${id}`, { status, moderation_note: note });
      toast.success(status === "published" ? t("approved") : t("updated")); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };
  const editReport = async (id, patch) => {
    try { await api.patch(`/reports/${id}`, patch); toast.success(t("report_updated")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };
  const deleteReport = async (id) => {
    try { await api.delete(`/reports/${id}`); toast.success(t("report_deleted")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };
  const reopenReport = async (id) => {
    try { await api.patch(`/reports/${id}`, { status: "published" }); toast.success(t("report_reopened")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };

  // ---- Bulk operations on reports (Lotto 3 #7) ----
  const bulkDeleteReports = async () => {
    const ids = Array.from(selectedReports);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/reports/bulk-delete", { report_ids: ids });
      toast.success(t("bulk_deleted_ok").replace("{n}", String(data.deleted ?? ids.length)));
      clearReportSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("generic_error"));
    } finally { setBulkBusy(false); }
  };
  const bulkSetReportsStatus = async (status) => {
    const ids = Array.from(selectedReports);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/reports/bulk-status", { report_ids: ids, status });
      toast.success(t("bulk_updated_ok").replace("{n}", String(data.modified ?? ids.length)));
      clearReportSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("generic_error"));
    } finally { setBulkBusy(false); }
  };
  const moderateAlert = async (id, state) => {
    try { await api.patch(`/urgent-alerts/${id}`, { state });
      toast.success(t(state === "active" ? "urgent_approved" : state === "rejected" ? "urgent_rejected" : "updated")); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };
  const moderatePhoto = async (rid, pid, status) => {
    try { await api.patch(`/reports/${rid}/photos/${pid}`, { status });
      toast.success(status === "published" ? t("approved") : t("updated")); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };

  // Lotto 10 — Moderazione Admin: applica un'azione a UNA singola riga foto,
  // usando la logica identica alle Verdi/Gialle/Rosse (PATCH reports oppure
  // moderate della foto aggiuntiva, mai duplicazione dei dati originali).
  const photoAction = async (p, action, riskLevel = null) => {
    // action ∈ 'approve' | 'reject' | 'archive' | 'reopen' | 'delete'
    // Per source='new_report': la moderazione agisce sul report parent.
    // Per source='added_photo': agisce sulla foto aggiuntiva del report.
    // Per source='urgent'|'extension': agisce sull'urgent alert (legacy).
    if (p.source === "new_report") {
      if (action === "delete") return api.delete(`/reports/${p.origin_id}`);
      const statusMap = { approve: "published", reject: "rejected", archive: "archived", reopen: "published" };
      const patch = { status: statusMap[action] };
      if (action === "approve" && riskLevel) patch.risk_level = riskLevel;
      return api.patch(`/reports/${p.origin_id}`, patch);
    }
    if (p.source === "added_photo") {
      const statusMap = { approve: "published", reject: "rejected", archive: "rejected", reopen: "pending_moderation" };
      return api.patch(`/reports/${p.origin_id}/photos/${p.photo_id}`, { status: statusMap[action] });
    }
    if (p.source === "urgent" || p.source === "extension") {
      const stateMap = { approve: "active", reject: "rejected", archive: "archived", reopen: "active" };
      return api.patch(`/urgent-alerts/${p.origin_id}`, { state: stateMap[action] });
    }
    throw new Error("unsupported source");
  };

  const runPhotoSingleAction = async (p, action, riskLevel = null) => {
    setPhBulkBusy(true);
    try {
      await photoAction(p, action, riskLevel);
      if (action === "approve" && riskLevel) {
        toast.success(t("photo_approved_as_color").replace("{color}", t(riskLevel) || riskLevel));
      } else {
        const okMap = { approve: t("approved"), reject: t("rejected"), archive: t("archived") || "Archiviato", reopen: t("reopened") || "Riaperto", delete: t("deleted") || "Eliminato" };
        toast.success(okMap[action] || t("updated"));
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    } finally { setPhBulkBusy(false); }
  };

  const bulkPhotoAction = async (action) => {
    const keys = Array.from(selectedPhotos);
    if (keys.length === 0) return;
    if (action === "delete" && !window.confirm(t("bulk_delete_confirm_body").replace("{n}", String(keys.length)))) return;
    setPhBulkBusy(true);
    const targets = keys.map((k) => photosReview.find((p) => photoKey(p) === k)).filter(Boolean);
    let ok = 0, ko = 0;
    for (const p of targets) {
      try { await photoAction(p, action); ok++; }
      catch (e) { ko++; }
    }
    if (ok > 0) toast.success(t("bulk_updated_ok").replace("{n}", String(ok)));
    if (ko > 0) toast.error(`${ko} ${t("errors") || "errori"}`);
    clearPhotoSelection();
    await load();
    setPhBulkBusy(false);
  };
  const setRole = async (uid, role) => {
    try { await api.patch(`/admin/users/${uid}/role`, { role }); toast.success(t("role_updated")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };
  const flagUser = async (uid, patch) => {
    try { await api.patch(`/admin/users/${uid}/flag`, patch); toast.success(t("user_updated")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
  };

  // --- Counters for homepage ---
  const counts = useMemo(() => {
    const red = allReports.filter((r) => r.risk_level === "red").length;
    const yellow = allReports.filter((r) => r.risk_level === "yellow").length;
    const green = allReports.filter((r) => r.risk_level === "green").length;
    const photos = photosReview.length;
    const eventsPending = events.filter((e) => e.state === "pending_moderation").length;
    const eventsTotal = events.length;
    const mkActive = (listings || []).filter((l) => l.state === "published").length;
    const mkTotal = (listings || []).length;
    return { red, yellow, green, photos, eventsPending, eventsTotal, mkActive, mkTotal };
  }, [allReports, photosReview, events, listings]);

  // --- Filtering for reports section ---
  const filteredReports = useMemo(() => {
    const q = (searchQ || "").toLowerCase();
    return allReports.filter((r) => {
      if (colorFocus && r.risk_level !== colorFocus) return false;
      if (colorFilter.length > 0 && !colorFilter.includes(r.risk_level)) return false;
      if (statusBuckets.length > 0) {
        const ok = statusBuckets.some((k) => STATUS_BUCKETS.find((b) => b.k === k)?.match(r));
        if (!ok) return false;
      }
      if (filterCategory !== "all" && r.category !== filterCategory) return false;
      if (filterDate) {
        try { if (!(r.created_at || "").startsWith(filterDate)) return false; } catch {}
      }
      if (filterAiConf > 0) {
        const c = (r.ai_analysis || {}).confidence;
        if (typeof c !== "number" || c * 100 < filterAiConf) return false;
      }
      if (filterSensitive && !r.contains_sensitive) return false;
      if (filterLegal && !((r.ai_analysis || {}).legal_issues)) return false;
      if (q) {
        const hay = [r.description, r.author_name, r.report_id, r.subcategory, r.category].map((s) => (s || "").toLowerCase()).join(" ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allReports, colorFocus, colorFilter, statusBuckets, filterCategory, filterDate, filterAiConf, filterSensitive, filterLegal, searchQ]);

  // Bulk selection helpers (Lotto 3 #7). Placed with the other useMemo hooks
  // so the React "rules-of-hooks" linter accepts them (they must be at the
  // top of the component body, never inside inner arrow-defined sections).
  const visibleReportIds = useMemo(
    () => filteredReports.slice(0, 100).map((r) => r.report_id),
    [filteredReports],
  );
  const allVisibleReportsSelected = visibleReportIds.length > 0 && visibleReportIds.every((id) => selectedReports.has(id));
  const toggleSelectAllReports = () => {
    if (allVisibleReportsSelected) clearReportSelection();
    else setSelectedReports(new Set(visibleReportIds));
  };

  // Filtered events (Lotto 6 #7) — kept at the top of the component so the
  // React "rules-of-hooks" linter is happy.
  const filteredEvents = useMemo(() => {
    let arr = [...events];
    if (evFilterState.length > 0) arr = arr.filter((e) => evFilterState.includes(e.state));
    return arr;
  }, [events, evFilterState]);

  // Lotto 8 — bulk selection on Extraordinary Events
  const [selectedEvents, setSelectedEvents] = useState(() => new Set());
  const [evBulkBusy, setEvBulkBusy] = useState(false);
  const [evEditModal, setEvEditModal] = useState(null); // event obj being edited
  const toggleEventSelected = (id) => setSelectedEvents((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const clearEventSelection = () => setSelectedEvents(new Set());
  const visibleEventIds = filteredEvents.map((e) => e.event_id);
  const allVisibleEventsSelected = visibleEventIds.length > 0 && visibleEventIds.every((id) => selectedEvents.has(id));
  const toggleSelectAllEvents = () => {
    if (allVisibleEventsSelected) clearEventSelection();
    else setSelectedEvents(new Set(visibleEventIds));
  };
  const evBulkAction = async (action) => {
    const ids = Array.from(selectedEvents);
    if (ids.length === 0) return;
    let reason = null;
    if (action === "reject") reason = window.prompt(t("reject_reason_prompt") || "Motivazione:", "");
    setEvBulkBusy(true);
    try {
      const { data } = await api.post("/admin/events/bulk-action", { event_ids: ids, action, reason });
      toast.success(t("bulk_updated_ok").replace("{n}", String(data.modified ?? ids.length)));
      clearEventSelection();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    } finally { setEvBulkBusy(false); }
  };
  const evBulkDelete = async () => {
    const ids = Array.from(selectedEvents);
    if (ids.length === 0) return;
    setEvBulkBusy(true);
    try {
      const { data } = await api.post("/admin/events/bulk-delete", { event_ids: ids });
      toast.success(t("bulk_deleted_ok").replace("{n}", String(data.deleted ?? ids.length)));
      clearEventSelection();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    } finally { setEvBulkBusy(false); }
  };

  const filteredPhotos = useMemo(() => {
    return photosReview.filter((p) => {
      if (phStatus.length > 0 && !phStatus.includes(p.status)) return false;
      if (phReason.length > 0) {
        const rs = p.review_reasons || [];
        if (!phReason.some((r) => rs.includes(r))) return false;
      }
      if (phSource.length > 0 && !phSource.includes(p.source)) return false;
      if (phAuthor) {
        if (!(p.author_name || "").toLowerCase().includes(phAuthor.toLowerCase())) return false;
      }
      if (colorFilter.length > 0 && !colorFilter.includes(p.color)) return false;
      if (filterCategory !== "all" && p.category !== filterCategory) return false;
      return true;
    });
  }, [photosReview, phStatus, phReason, phSource, phAuthor, colorFilter, filterCategory]);

  // Lotto 10 — helpers "seleziona tutti" identici a Reports
  const visiblePhotoKeys = filteredPhotos.map(photoKey);
  const allVisiblePhotosSelected = visiblePhotoKeys.length > 0 && visiblePhotoKeys.every((k) => selectedPhotos.has(k));
  const toggleSelectAllPhotos = () => {
    if (allVisiblePhotosSelected) clearPhotoSelection();
    else setSelectedPhotos(new Set(visiblePhotoKeys));
  };

  // Lotto 11 — Marketplace admin management (same pattern as Verdi/Gialle/Rosse)
  const filteredListings = useMemo(() => {
    const q = (mkSearchQ || "").toLowerCase().trim();
    return (listings || []).filter((l) => {
      if (mkFilterType && l.type !== mkFilterType) return false;
      if (mkFilterState.length > 0 && !mkFilterState.includes(l.state)) return false;
      if (q) {
        const hay = [l.title, l.description, l.owner_name, l.category, l.subcategory, l.item_type, l.province_name].map((s) => (s || "").toLowerCase()).join(" ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [listings, mkFilterType, mkFilterState, mkSearchQ]);

  const visibleListingIds = filteredListings.map((l) => l.listing_id);
  const allVisibleListingsSelected = visibleListingIds.length > 0 && visibleListingIds.every((id) => selectedListings.has(id));
  const toggleSelectAllListings = () => {
    if (allVisibleListingsSelected) clearListingSelection();
    else setSelectedListings(new Set(visibleListingIds));
  };

  const mkAction = async (id, action) => {
    setMkBulkBusy(true);
    try {
      if (action === "archive") await api.post(`/admin/marketplace/listings/${id}/archive`);
      else if (action === "reactivate") await api.post(`/admin/marketplace/listings/${id}/reactivate`);
      else if (action === "delete") await api.delete(`/admin/marketplace/listings/${id}`);
      const okMap = { archive: t("archived") || "Archiviato", reactivate: t("reactivated") || "Riattivato", delete: t("deleted") || "Eliminato" };
      toast.success(okMap[action] || t("updated"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    } finally { setMkBulkBusy(false); }
  };

  const mkBulkAction = async (action) => {
    const ids = Array.from(selectedListings);
    if (ids.length === 0) return;
    setMkBulkBusy(true);
    try {
      const { data } = await api.post("/admin/marketplace/bulk-action", { listing_ids: ids, action });
      const count = action === "delete" ? (data.deleted ?? ids.length) : (data.modified ?? ids.length);
      toast.success(action === "delete"
        ? t("bulk_deleted_ok").replace("{n}", String(count))
        : t("bulk_updated_ok").replace("{n}", String(count)));
      clearListingSelection();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    } finally { setMkBulkBusy(false); }
  };

  const openListingDetail = async (l) => {
    try {
      const { data } = await api.get(`/marketplace/listings/${l.listing_id}/found-checks`);
      setListingDetail({ listing: l, checksData: data });
    } catch (e) {
      setListingDetail({ listing: l, checksData: null });
    }
  };

  const filteredUrgent = useMemo(() => {
    return urgentAlerts.filter((a) => {
      if (urgentFilter.length === 0) return true;
      const st = a.computed_state || a.state;
      if (urgentFilter.includes("ext_pending")) {
        if ((a.pending_extensions || []).some((e) => e.status === "pending")) return true;
      }
      return urgentFilter.includes(st);
    });
  }, [urgentAlerts, urgentFilter]);

  const filteredUserReports = useMemo(() => {
    return userReports.filter((u) => {
      if (urFilterStatus.length > 0 && !urFilterStatus.includes(u.status)) return false;
      if (urFilterReason !== "all" && u.reason !== urFilterReason) return false;
      return true;
    });
  }, [userReports, urFilterStatus, urFilterReason]);

  const filteredContentReports = useMemo(() => {
    return contentReports.filter((r) => {
      if (crFilterStatus.length > 0 && !crFilterStatus.includes(r.status)) return false;
      if (crFilterReason !== "all" && r.reason !== crFilterReason) return false;
      if (crFilterTarget !== "all" && r.target_type !== crFilterTarget) return false;
      return true;
    });
  }, [contentReports, crFilterStatus, crFilterReason, crFilterTarget]);

  if (!user) return null;

  const resetAll = () => {
    setStatusBuckets([]); setColorFilter([]); setSearchQ(""); setFilterCategory("all");
    setFilterDate(""); setFilterAiConf(0); setFilterSensitive(false); setFilterLegal(false);
    setPhStatus([]); setPhReason([]); setPhSource([]); setPhAuthor("");
    setUrgentFilter([]);
  };

  const openSection = (name, color) => { setSection(name); setColorFocus(color || null); };

  // ---- HOME ----
  const Home = () => (
    <div data-testid="admin-home">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-emerald-700 font-semibold"><Shield size={12} /> {t("admin_dashboard_kicker")}</div>
      <h1 className="font-heading text-4xl font-bold text-slate-900 mt-2">{t("admin_home_title")}</h1>
      <p className="text-sm text-slate-600 mt-1">{t("admin_home_desc")}</p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-8" data-testid="admin-home-cards">
        <HomeCard testid="home-card-red" title={t("admin_card_red")} count={counts.red} color="#EF4444" icon={AlertOctagon} onClick={() => openSection("reports", "red")} />
        <HomeCard testid="home-card-yellow" title={t("admin_card_yellow")} count={counts.yellow} color="#EAB308" icon={FileWarning} onClick={() => openSection("reports", "yellow")} />
        <HomeCard testid="home-card-green" title={t("admin_card_green")} count={counts.green} color="#10B981" icon={CheckCircle2} onClick={() => openSection("reports", "green")} />
        <HomeCard testid="home-card-photos" title={t("admin_card_photos_review")} count={counts.photos} color="#F97316" icon={Camera} onClick={() => setSection("photos")} />
        <HomeCard testid="home-card-events" title={t("admin_card_events") || t("admin_tab_events")} count={counts.eventsPending} secondary={counts.eventsTotal ? `${counts.eventsTotal} ${t("total") || "totali"}` : null} color="#DC2626" icon={AlertOctagon} onClick={() => setSection("events")} />
        <HomeCard testid="home-card-marketplace" title={t("admin_card_marketplace") || "Marketplace"} count={counts.mkActive} secondary={counts.mkTotal ? `${counts.mkTotal} ${t("total") || "totali"}` : null} color="#A78BFA" icon={ScrollText} onClick={() => setSection("marketplace")} />
      </div>

      <div className="mt-8">
        <button
          data-testid="admin-open-others"
          onClick={() => setSection("others")}
          className="w-full flex items-center justify-between bg-white border border-slate-200 rounded-2xl p-4 hover:border-emerald-400"
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("admin_section_others")}</div>
            <div className="text-sm text-slate-800 mt-0.5">{t("admin_others_desc")}</div>
          </div>
          <ChevronRight className="text-slate-400" />
        </button>
      </div>
    </div>
  );

  // ---- REPORTS SECTION ----
  const ReportsSection = () => (
    <div data-testid="admin-reports-section">
      <SectionHeader onBack={() => { setSection("home"); setColorFocus(null); clearReportSelection(); }} title={colorFocus ? t(`admin_card_${colorFocus}`) : t("tab_all_reports")} count={filteredReports.length} />
      <StatusBuckets buckets={statusBuckets} onToggle={(k) => toggleBucket(statusBuckets, k, setStatusBuckets)} t={t} testid="reports-buckets" />
      <GlobalFilters
        open={filtersOpen} setOpen={setFiltersOpen} advOpen={advOpen} setAdvOpen={setAdvOpen}
        searchQ={searchQ} setSearchQ={setSearchQ}
        colorFilter={colorFilter} toggleColor={toggleColor}
        filterCategory={filterCategory} setFilterCategory={setFilterCategory}
        filterDate={filterDate} setFilterDate={setFilterDate}
        filterAiConf={filterAiConf} setFilterAiConf={setFilterAiConf}
        filterSensitive={filterSensitive} setFilterSensitive={setFilterSensitive}
        filterLegal={filterLegal} setFilterLegal={setFilterLegal}
        onReset={resetAll} t={t}
      />
      {/* Bulk actions toolbar — sticky-ish. Only rendered when there are rows. */}
      {visibleReportIds.length > 0 && (
        <BulkReportsToolbar
          t={t}
          user={user}
          totalVisible={visibleReportIds.length}
          selectedCount={selectedReports.size}
          allSelected={allVisibleReportsSelected}
          onToggleAll={toggleSelectAllReports}
          onClear={clearReportSelection}
          onBulkDelete={bulkDeleteReports}
          onBulkSetStatus={bulkSetReportsStatus}
          busy={bulkBusy}
        />
      )}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4">
        {filteredReports.length === 0 ? (
          <div className="p-8 text-center text-slate-500">{t("no_reports")}</div>
        ) : filteredReports.slice(0, 100).map((r) => (
          <ReportRow key={r.report_id} r={r} t={t}
            showSensitive={showSensitive} setShowSensitive={setShowSensitive}
            user={user} mod={r.status === "pending_moderation"}
            editReport={editReport} decide={decide} deleteReport={deleteReport} reopenReport={reopenReport}
            selected={selectedReports.has(r.report_id)}
            onToggleSelected={() => toggleReportSelected(r.report_id)}
          />
        ))}
      </div>
    </div>
  );

  // ---- MODERAZIONE ADMIN (Lotto 10) ----
  // Ricostruita per usare la stessa logica delle Verdi/Gialle/Rosse:
  // header + filtri + BulkToolbar identico + checkbox per riga + azioni.
  const PhotosSection = () => (
    <div data-testid="admin-photos-section">
      <SectionHeader onBack={() => { setSection("home"); clearPhotoSelection(); }} title={t("photos_review_title")} count={filteredPhotos.length} />
      <div className="bg-white border border-slate-200 rounded-2xl p-4 mt-4 space-y-3" data-testid="photos-filters">
        <PhFilterChips title={t("admin_filter_status")} value={phStatus} onToggle={(k) => toggleBucket(phStatus, k, setPhStatus)}
          options={[{k:"pending",label:t("admin_st_pending_approval")},{k:"published",label:t("admin_st_approved")},{k:"rejected",label:t("admin_st_archived")}]} testid="ph-status" />
        <PhFilterChips title={t("photos_review_source")} value={phSource} onToggle={(k) => toggleBucket(phSource, k, setPhSource)}
          options={[{k:"new_report",label:t("src_new_report")},{k:"added_photo",label:t("src_added_photo")},{k:"urgent",label:t("src_urgent")},{k:"extension",label:t("src_extension")}]} testid="ph-source" />
        <PhFilterChips title={t("photos_review_reason")} value={phReason} onToggle={(k) => toggleBucket(phReason, k, setPhReason)}
          options={[
            {k:"photo_incoherent",label:t("reason_photo_incoherent")},
            {k:"photo_text_incoherent",label:t("reason_photo_text_incoherent")},
            {k:"category_mismatch",label:t("reason_category_mismatch")},
            {k:"severity_higher",label:t("reason_severity_higher")},
            {k:"sensitive_content",label:t("reason_sensitive_content")},
            {k:"legal_issues",label:t("reason_legal_issues")},
            {k:"low_ai_confidence",label:t("reason_low_ai_confidence")},
            {k:"face_blur_failed",label:t("reason_face_blur_failed")},
            {k:"face_blur_uncertain",label:t("reason_face_blur_uncertain")},
            {k:"possibly_criminal_content",label:t("reason_possibly_criminal_content") || "Possibile scena di reato"},
          ]} testid="ph-reason" />
        <div className="flex flex-wrap gap-2 items-center">
          <Input data-testid="ph-author-input" value={phAuthor} onChange={(e) => setPhAuthor(e.target.value)} placeholder={t("author_line_by") + "…"} className="h-9 rounded-full max-w-xs" />
          <ColorFilterBar colorFilter={colorFilter} toggleColor={toggleColor} onClear={() => setColorFilter([])} t={t} inline />
          <button data-testid="ph-reset" onClick={() => { setPhStatus([]); setPhReason([]); setPhSource([]); setPhAuthor(""); setColorFilter([]); }} className="text-xs px-3 h-8 rounded-full border border-slate-200 hover:bg-slate-100">{t("reset")}</button>
        </div>
      </div>

      {/* Toolbar bulk identica alle Verdi/Gialle/Rosse */}
      {visiblePhotoKeys.length > 0 && (
        <div className="mt-3 sticky top-0 z-10 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-2xl px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="bulk-photos-toolbar">
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
            <input type="checkbox" data-testid="bulk-photos-select-all"
              checked={allVisiblePhotosSelected} onChange={toggleSelectAllPhotos}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
            {t("bulk_select_all")}
          </label>
          <span className="text-xs text-slate-500 tabular-nums" data-testid="bulk-photos-count">
            {t("bulk_selected_of").replace("{n}", String(selectedPhotos.size)).replace("{total}", String(visiblePhotoKeys.length))}
          </span>
          {selectedPhotos.size > 0 && (
            <>
              <div className="mx-1 h-4 w-px bg-slate-200" />
              <Button size="sm" data-testid="bulk-photos-approve" disabled={phBulkBusy} onClick={() => bulkPhotoAction("approve")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white text-xs h-8">
                <CheckCircle2 size={13} className="mr-1" /> {t("approve")}
              </Button>
              <Button size="sm" variant="outline" data-testid="bulk-photos-reject" disabled={phBulkBusy} onClick={() => bulkPhotoAction("reject")} className="rounded-full text-xs h-8 hover:bg-red-50 hover:text-red-800">
                <XCircle size={13} className="mr-1" /> {t("reject")}
              </Button>
              <Button size="sm" variant="outline" data-testid="bulk-photos-archive" disabled={phBulkBusy} onClick={() => bulkPhotoAction("archive")} className="rounded-full text-xs h-8 hover:bg-slate-900 hover:text-white">
                {t("archive")}
              </Button>
              {user.role === "admin" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="bulk-photos-delete" disabled={phBulkBusy} className="rounded-full text-xs h-8 hover:bg-red-50 hover:text-red-800 text-red-700 border-red-200">
                      <Trash2 size={13} className="mr-1" /> {t("delete")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="bulk-photos-delete-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("bulk_delete_confirm_title")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("bulk_delete_confirm_body").replace("{n}", String(selectedPhotos.size))}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="bulk-photos-delete-cancel">{t("cancel")}</AlertDialogCancel>
                      <AlertDialogAction data-testid="bulk-photos-delete-confirm" onClick={() => bulkPhotoAction("delete")} className="bg-red-600 hover:bg-red-700 text-white">
                        {t("delete")} ({selectedPhotos.size})
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <button data-testid="bulk-photos-clear" onClick={clearPhotoSelection} className="text-xs text-slate-500 hover:text-slate-900 px-2 h-8 rounded-full hover:bg-slate-100 inline-flex items-center gap-1">
                <X size={12} /> {t("bulk_clear_selection")}
              </button>
            </>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4">
        {filteredPhotos.length === 0 ? (
          <div className="p-8 text-center text-slate-500">{t("photos_review_empty")}</div>
        ) : filteredPhotos.map((p) => {
          const key = photoKey(p);
          return (
            <PhotoReviewRow key={key} p={p} t={t} user={user}
              selected={selectedPhotos.has(key)}
              onToggleSelected={() => togglePhotoSelected(key)}
              onAction={(action, riskLevel) => runPhotoSingleAction(p, action, riskLevel)}
              busy={phBulkBusy}
            />
          );
        })}
      </div>
    </div>
  );

  // ---- URGENT SECTION ----
  const UrgentSection = () => (
    <div data-testid="admin-urgent-section">
      <SectionHeader onBack={() => setSection("home")} title={t("admin_card_urgent")} count={filteredUrgent.length} />
      <div className="mt-4 flex flex-wrap gap-2" data-testid="urgent-buckets">
        {URGENT_BUCKETS.map((b) => (
          <button key={b.k} data-testid={`urgent-bucket-${b.k}`} onClick={() => toggleBucket(urgentFilter, b.k, setUrgentFilter)}
            className={`h-8 px-3 rounded-full text-xs font-semibold border-2 ${urgentFilter.includes(b.k) ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
            {t(b.labelKey)}
          </button>
        ))}
        {urgentFilter.length > 0 && (
          <button data-testid="urgent-bucket-clear" onClick={() => setUrgentFilter([])} className="h-8 px-3 rounded-full text-xs text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1"><RotateCcw size={11} /> {t("reset")}</button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4" data-testid="urgent-tab-content">
        {filteredUrgent.length === 0 && <div className="p-8 text-center text-slate-500">{t("no_urgent_alerts")}</div>}
        {filteredUrgent.map((a) => {
          const st = a.computed_state || a.state;
          const stColor = st === "active" ? "bg-red-100 text-red-800" : st === "pending_approval" ? "bg-yellow-100 text-yellow-800" : st === "expired_to_verify" ? "bg-amber-100 text-amber-800" : st === "resolved" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700";
          const stLabelKey = st === "expired_to_verify" ? "admin_urgent_state_expired" : st === "resolved" ? "urgent_state_resolved" : st === "archived" ? "admin_urgent_state_archived" : st === "pending_approval" ? "admin_urgent_state_pending" : st === "rejected" ? "admin_urgent_state_rejected" : "admin_urgent_state_active";
          const pendingExt = (a.pending_extensions || []).filter((e) => e.status === "pending");
          return (
            <div key={a.alert_id} className="p-4 border-t border-slate-100" data-testid={`urgent-row-${a.alert_id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-red-600 text-white border-0 uppercase tracking-widest text-[10px] font-semibold">urgent</Badge>
                <Badge className={`${stColor} border-0`}>{t(stLabelKey)}</Badge>
                <Badge variant="outline">{t(`cat_${a.category}`) || a.category}</Badge>
                <Badge variant="outline">{a.subcategory === "other_urgent" ? t("urgent_other") : (t(`sub_${a.subcategory}`) || a.subcategory)}</Badge>
                <span className="text-xs text-slate-500">{a.duration_hours}h · {a.radius_m}m · {new Date(a.created_at).toLocaleString()}</span>
                {pendingExt.length > 0 && <Badge className="bg-amber-100 text-amber-800 border-0">+{pendingExt.length} {t("admin_urgent_ext_pending")}</Badge>}
              </div>
              <div className="mt-2 font-semibold text-slate-900 break-words">{a.description}</div>
              {a.other_subcategory && <div className="text-xs text-slate-600 italic mt-1">{a.other_subcategory}</div>}
              <div className="mt-1 text-xs text-slate-500">{t("author_line_by")} {displayAuthorName(a, t)} · {a.lat?.toFixed(4)}, {a.lng?.toFixed(4)} · seconds_remaining: {a.seconds_remaining ?? "-"}</div>
              {(a.photos || []).slice(0, 3).map((p, i) => (
                <FullscreenMedia key={i} path={p} testid={`urg-photo-fs-${a.alert_id}-${i}`}>
                  <button className="w-16 h-16 rounded-lg overflow-hidden mt-2 mr-2 inline-block border border-slate-200"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-full h-full object-cover" /></button>
                </FullscreenMedia>
              ))}
              <div className="mt-3 flex flex-wrap gap-2">
                {st === "pending_approval" && (
                  <>
                    <Button data-testid={`urgent-approve-${a.alert_id}`} onClick={() => moderateAlert(a.alert_id, "active")} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white"><CheckCircle2 size={14} className="mr-1" /> {t("approve_urgent")}</Button>
                    <Button data-testid={`urgent-reject-${a.alert_id}`} onClick={() => moderateAlert(a.alert_id, "rejected")} size="sm" variant="outline" className="rounded-full hover:bg-red-50 hover:text-red-800"><XCircle size={14} className="mr-1" /> {t("reject_urgent")}</Button>
                  </>
                )}
                {st === "active" && <Button size="sm" variant="outline" className="rounded-full hover:bg-slate-100" onClick={() => moderateAlert(a.alert_id, "archived")}>{t("archive")}</Button>}
                {(st === "archived" || st === "rejected" || st === "expired_to_verify") && <Button size="sm" variant="outline" className="rounded-full hover:bg-emerald-50 hover:text-emerald-800" onClick={() => moderateAlert(a.alert_id, "active")}><RotateCcw size={14} className="mr-1" /> {t("reopen")}</Button>}
                <Link to={`/urgent/${a.alert_id}`} className="text-xs text-emerald-700 hover:underline self-center ml-2">{t("open_details") || "Apri"}</Link>
              </div>
              {pendingExt.map((ext) => (
                <div key={ext.extension_id} className="mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid={`ext-row-${ext.extension_id}`}>
                  <div className="text-xs text-slate-700"><b>+{ext.hours}h</b> · {ext.reason} · {ext.requested_by_name}</div>
                  <div className="mt-2 flex gap-2">
                    <Button data-testid={`ext-approve-${ext.extension_id}`} onClick={async () => { try { await api.patch(`/urgent-alerts/${a.alert_id}/extensions/${ext.extension_id}`, { status: "approved" }); toast.success(t("urgent_approved")); load(); } catch(e){ toast.error(e.response?.data?.detail || t("generic_error")); } }} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white"><CheckCircle2 size={14} className="mr-1" />{t("approve")}</Button>
                    <Button data-testid={`ext-reject-${ext.extension_id}`} onClick={async () => { try { await api.patch(`/urgent-alerts/${a.alert_id}/extensions/${ext.extension_id}`, { status: "rejected" }); toast.success(t("urgent_rejected")); load(); } catch(e){ toast.error(e.response?.data?.detail || t("generic_error")); } }} size="sm" variant="outline" className="rounded-full hover:bg-red-50 hover:text-red-800"><XCircle size={14} className="mr-1" />{t("reject")}</Button>
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ---- OTHERS ----
  const OthersSection = () => (
    <div data-testid="admin-others-section">
      <SectionHeader onBack={() => setSection("home")} title={t("admin_section_others")} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <OtherLink testid="others-users" title={t("admin_tab_users")} onClick={() => setSection("users")} icon={Users} />
        <OtherLink testid="others-user-reports" title={`${t("admin_tab_user_reports")}${userReports.length ? ` (${userReports.length})` : ""}`} onClick={() => setSection("user_reports")} icon={Flag} />
        <OtherLink testid="others-content-reports" title={`${t("admin_tab_content_reports")}${contentReports.length ? ` (${contentReports.length})` : ""}`} onClick={() => setSection("content_reports")} icon={Flag} />
        <OtherLink testid="others-events" title={`${t("admin_tab_events")}${events.filter((e) => e.state === "pending_moderation").length ? ` (${events.filter((e) => e.state === "pending_moderation").length})` : ""}`} onClick={() => setSection("events")} icon={AlertTriangle} />
        <OtherLink testid="others-reforestation" title={t("admin_reforestation_manage")} onClick={() => nav("/reforestation")} icon={Palette} />
        <OtherLink testid="others-feedback" title={t("admin_tab_feedback")} onClick={() => setSection("feedback")} icon={MessageSquare} />
        {user.role === "admin" && <OtherLink testid="others-donations" title={t("admin_tab_donations")} onClick={() => setSection("donations")} icon={HandCoins} />}
        {user.role === "admin" && <OtherLink testid="others-logs" title={t("admin_tab_logs")} onClick={() => setSection("logs")} icon={ScrollText} />}
        {user.role === "admin" && <OtherLink testid="others-verified-entities" title={t("admin_verified_entities_super")} onClick={() => setSection("verified_entities")} icon={Shield} />}
        {user.role === "admin" && <OtherLink testid="others-admin-mgmt" title={t("admin_management_super")} onClick={() => setSection("admin_mgmt")} icon={Shield} />}
        {user.role === "admin" && <OtherLink testid="others-super-admin-pwd" title={t("admin_reset_super_password")} onClick={async () => {
          if (!window.confirm(t("admin_reset_super_password_confirm"))) return;
          try {
            const { data } = await api.post("/admin/pwd/generate-reset-link", {});
            window.location.assign(data.reset_path);
          } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
        }} icon={Shield} />}
        {user.role === "admin" && <OtherLink testid="others-content-pages" title={t("admin_content_pages")} onClick={() => nav("/admin/content-pages")} icon={ScrollText} />}
        {user.role === "admin" && <OtherLink testid="others-pec-config" title={t("admin_pec_config")} onClick={() => setSection("pec_config")} icon={ScrollText} />}
      </div>
    </div>
  );

  const UserReportsSection = () => {
    const patchUR = async (id, patch) => {
      try { await api.patch(`/admin/user-reports/${id}`, patch); toast.success(t("updated")); load(); }
      catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
    };
    const actionUR = async (id, action, hours, note) => {
      // Significant restrictions require a motivation shown to the affected
      // user in their language. If the caller didn't provide one we prompt
      // here to avoid the 400 from the backend.
      const SIGNIFICANT = new Set(["limit_chat", "limit_publish", "suspend", "ban"]);
      let finalNote = note;
      if (SIGNIFICANT.has(action) && (!finalNote || finalNote.trim().length < 10)) {
        finalNote = window.prompt(t("ur_note_required_prompt") || "Motivazione (obbligatoria, min 10 caratteri):");
        if (!finalNote || finalNote.trim().length < 10) {
          toast.error(t("ur_note_required_toast") || "Motivazione obbligatoria (min 10 caratteri).");
          return;
        }
      }
      try { await api.post(`/admin/user-reports/${id}/action`, { action, hours, note: finalNote }); toast.success(t("updated")); load(); }
      catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
    };
    const STATUSES = [
      { k: "new", labelKey: "ur_status_new" },
      { k: "in_review", labelKey: "ur_status_in_review" },
      { k: "resolved", labelKey: "ur_status_resolved" },
      { k: "archived", labelKey: "ur_status_archived" },
      { k: "rejected", labelKey: "ur_status_rejected" },
    ];
    const REASONS = ["spam","offensive","harassment","misleading","bad_chat","misuse","false_reports","other"];
    return (
      <div data-testid="admin-user-reports-section">
        <SectionHeader onBack={() => setSection("others")} title={t("admin_tab_user_reports")} count={filteredUserReports.length} />
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mt-4 space-y-3" data-testid="ur-filters">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">{t("admin_filter_status")}</div>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <button key={s.k} data-testid={`ur-status-${s.k}`} onClick={() => toggleBucket(urFilterStatus, s.k, setUrFilterStatus)}
                  className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border ${urFilterStatus.includes(s.k) ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
                  {t(s.labelKey)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("report_user_reason")}</div>
            <Select value={urFilterReason} onValueChange={setUrFilterReason}>
              <SelectTrigger className="h-8 text-xs rounded-lg w-56" data-testid="ur-reason-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("ur_status_all")}</SelectItem>
                {REASONS.map((r) => <SelectItem key={r} value={r}>{t(`ur_reason_${r}`)}</SelectItem>)}
              </SelectContent>
            </Select>
            <button data-testid="ur-reset" onClick={() => { setUrFilterStatus([]); setUrFilterReason("all"); }} className="text-xs px-3 h-8 rounded-full border border-slate-200 hover:bg-slate-100">{t("reset")}</button>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4">
          {filteredUserReports.length === 0 ? (
            <div className="p-8 text-center text-slate-500">{t("ur_no_items")}</div>
          ) : filteredUserReports.map((r) => (
            <div key={r.user_report_id} className="p-4 border-t border-slate-100 first:border-t-0" data-testid={`ur-row-${r.user_report_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-red-100 text-red-800 border-0 uppercase text-[10px]">{t(`ur_reason_${r.reason}`) || r.reason}</Badge>
                <Badge className="bg-slate-100 text-slate-700 border-0 uppercase text-[10px]">{t(`ur_status_${r.status}`) || r.status}</Badge>
                {typeof r.reported_user_report_count === "number" && r.reported_user_report_count > 1 && (
                  <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px]">{t("ur_reports_count")}: {r.reported_user_report_count}</Badge>
                )}
                <span className="text-xs text-slate-500 ml-auto">{new Date(r.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-2 text-sm text-slate-900">
                <span className="text-slate-500">Segnalato:</span> <b>{r.reported_user_name || r.reported_user_id}</b>
                <span className="text-slate-500 ml-2">Da:</span> <b>{r.reporter_name || "—"}</b>
              </div>
              {r.explanation && <div className="mt-1 text-sm text-slate-700 italic break-words">"{r.explanation}"</div>}
              {r.context_type && r.context_ref && (
                <div className="mt-2">
                  <Link data-testid={`ur-open-context-${r.user_report_id}`} to={r.context_type === "report" || r.context_type === "chat" || r.context_type === "message" ? `/report/${r.context_ref.split(":")[0]}` : `/profile`} className="text-xs text-emerald-700 hover:underline">
                    {t("ur_open_context")} · {r.context_type}
                  </Link>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {r.status !== "in_review" && <Button data-testid={`ur-review-${r.user_report_id}`} onClick={() => patchUR(r.user_report_id, { status: "in_review" })} size="sm" variant="outline" className="rounded-full text-xs hover:bg-slate-900 hover:text-white">Prendi in carico</Button>}
                {user.role === "admin" && (
                  <>
                    <Button data-testid={`ur-warn-${r.user_report_id}`} onClick={() => actionUR(r.user_report_id, "warn", null, null)} size="sm" variant="outline" className="rounded-full text-xs hover:bg-yellow-50 hover:text-yellow-800">{t("ur_action_warn")}</Button>
                    <Button data-testid={`ur-limit-chat-${r.user_report_id}`} onClick={() => actionUR(r.user_report_id, "limit_chat", 24, null)} size="sm" variant="outline" className="rounded-full text-xs hover:bg-orange-50 hover:text-orange-800">{t("ur_action_limit_chat")} 24h</Button>
                    <Button data-testid={`ur-suspend-${r.user_report_id}`} onClick={() => actionUR(r.user_report_id, "suspend", 72, null)} size="sm" variant="outline" className="rounded-full text-xs hover:bg-yellow-100 hover:text-yellow-900">{t("ur_action_suspend")} 72h</Button>
                    <Button data-testid={`ur-ban-${r.user_report_id}`} onClick={() => actionUR(r.user_report_id, "ban", null, null)} size="sm" variant="outline" className="rounded-full text-xs hover:bg-red-600 hover:text-white hover:border-red-600"><Ban size={12} className="mr-1" />{t("ur_action_ban")}</Button>
                  </>
                )}
                <Button data-testid={`ur-close-${r.user_report_id}`} onClick={() => actionUR(r.user_report_id, "close", null, null)} size="sm" variant="outline" className="rounded-full text-xs hover:bg-slate-100">{t("ur_action_close")}</Button>
                <Button data-testid={`ur-archive-${r.user_report_id}`} onClick={() => patchUR(r.user_report_id, { status: "archived" })} size="sm" variant="outline" className="rounded-full text-xs hover:bg-slate-100">{t("archive")}</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const ContentReportsSection = () => {
    // Content reports (POST /content-reports) target a piece of content —
    // report, marketplace listing, or a specific message. Admins can move a
    // report through the pipeline (open → in_review → resolved | dismissed).
    // Removal of the underlying content is done via the existing per-content
    // admin tools (Reports admin section for reports, marketplace-detail edit
    // for listings) so this section is intentionally free of duplicated
    // moderation actions.
    const patchCR = async (id, status) => {
      try {
        await api.patch(`/admin/content-reports/${id}`, { status });
        toast.success(t("updated"));
        load();
      } catch (e) { toast.error(e.response?.data?.detail || t("generic_error")); }
    };
    const STATUSES = [
      { k: "open", labelKey: "cr_status_open" },
      { k: "in_review", labelKey: "cr_status_in_review" },
      { k: "resolved", labelKey: "cr_status_resolved" },
      { k: "dismissed", labelKey: "cr_status_dismissed" },
    ];
    const REASONS = ["harassment","hate","sexual","spam","privacy","illegal","impersonation","abuse","other"];
    const TARGETS = ["report","marketplace_listing","message","marketplace_message"];
    return (
      <div data-testid="admin-content-reports-section">
        <SectionHeader onBack={() => setSection("others")} title={t("admin_tab_content_reports")} count={filteredContentReports.length} />
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mt-4 space-y-3" data-testid="cr-filters">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">{t("admin_filter_status")}</div>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <button key={s.k} data-testid={`cr-status-${s.k}`} onClick={() => toggleBucket(crFilterStatus, s.k, setCrFilterStatus)}
                  className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border ${crFilterStatus.includes(s.k) ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
                  {t(s.labelKey)}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("report_user_reason") || "Motivo"}</div>
            <Select value={crFilterReason} onValueChange={setCrFilterReason}>
              <SelectTrigger className="h-8 text-xs rounded-lg w-56" data-testid="cr-reason-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("ur_status_all") || "Tutti"}</SelectItem>
                {REASONS.map((r) => <SelectItem key={r} value={r}>{t(`report_reason_${r}`)}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold ml-2">{t("cr_target_type") || "Tipo"}</div>
            <Select value={crFilterTarget} onValueChange={setCrFilterTarget}>
              <SelectTrigger className="h-8 text-xs rounded-lg w-48" data-testid="cr-target-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("ur_status_all") || "Tutti"}</SelectItem>
                {TARGETS.map((tg) => <SelectItem key={tg} value={tg}>{t(`cr_target_${tg}`)}</SelectItem>)}
              </SelectContent>
            </Select>
            <button data-testid="cr-reset" onClick={() => { setCrFilterStatus([]); setCrFilterReason("all"); setCrFilterTarget("all"); }} className="text-xs px-3 h-8 rounded-full border border-slate-200 hover:bg-slate-100">{t("reset")}</button>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4">
          {filteredContentReports.length === 0 ? (
            <div className="p-8 text-center text-slate-500" data-testid="cr-empty">{t("cr_no_items")}</div>
          ) : filteredContentReports.map((r) => (
            <div key={r.content_report_id} className="p-4 border-t border-slate-100 first:border-t-0" data-testid={`cr-row-${r.content_report_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-red-100 text-red-800 border-0 uppercase text-[10px]">{t(`report_reason_${r.reason}`) || r.reason}</Badge>
                <Badge className="bg-slate-100 text-slate-700 border-0 uppercase text-[10px]">{t(`cr_status_${r.status}`) || r.status}</Badge>
                <Badge className="bg-blue-100 text-blue-800 border-0 uppercase text-[10px]">{t(`cr_target_${r.target_type}`) || r.target_type}</Badge>
                <span className="text-xs text-slate-500 ml-auto">{new Date(r.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-2 text-sm text-slate-900">
                <span className="text-slate-500">{t("cr_reported_by") || "Segnalato da"}:</span>{" "}
                <b>{r.reporter_deleted ? (t("cr_reporter_deleted") || "utente eliminato") : (r.reporter_name || "—")}</b>
                {r.target_author_id && (
                  <>
                    <span className="text-slate-500 ml-2">{t("cr_content_author") || "Autore contenuto"}:</span>{" "}
                    <Link to={`/profile/${r.target_author_id}`} className="text-emerald-700 hover:underline" data-testid={`cr-author-${r.content_report_id}`}>{r.target_author_id}</Link>
                  </>
                )}
              </div>
              {r.target_snippet && (
                <div className="mt-1 text-sm text-slate-700 italic break-words">"{r.target_snippet}"</div>
              )}
              {r.details && (
                <div className="mt-1 text-xs text-slate-500 break-words">
                  <span className="uppercase tracking-widest font-semibold text-slate-400">{t("report_content_details")}: </span>{r.details}
                </div>
              )}
              {r.target_link && (
                <div className="mt-2">
                  <Link to={r.target_link} data-testid={`cr-open-${r.content_report_id}`} className="text-xs text-emerald-700 hover:underline">
                    {t("cr_open_target") || "Apri contenuto segnalato"} →
                  </Link>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {r.status !== "in_review" && r.status !== "resolved" && r.status !== "dismissed" && (
                  <Button data-testid={`cr-review-${r.content_report_id}`} onClick={() => patchCR(r.content_report_id, "in_review")} size="sm" variant="outline" className="rounded-full text-xs hover:bg-slate-900 hover:text-white">{t("cr_action_review") || "Prendi in carico"}</Button>
                )}
                {r.status !== "resolved" && (
                  <Button data-testid={`cr-resolve-${r.content_report_id}`} onClick={() => patchCR(r.content_report_id, "resolved")} size="sm" variant="outline" className="rounded-full text-xs hover:bg-emerald-50 hover:text-emerald-800">{t("cr_action_resolve") || "Segna come risolto"}</Button>
                )}
                {r.status !== "dismissed" && (
                  <Button data-testid={`cr-dismiss-${r.content_report_id}`} onClick={() => patchCR(r.content_report_id, "dismissed")} size="sm" variant="outline" className="rounded-full text-xs hover:bg-slate-100">{t("cr_action_dismiss") || "Ignora"}</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };


  // ---- EVENTS SECTION (Lotto 6 #7) ----
  // Real admin/moderator management of Extraordinary Events. Reuses the
  // existing endpoints POST /events/{id}/approve, /reject, /conclude and
  // PATCH /events/{id} — no parallel moderation system is introduced.

  const evApprove = async (id) => {
    try { await api.post(`/events/${id}/approve`); toast.success(t("approved")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("generic_error")); }
  };
  const evReject = async (id) => {
    const reason = window.prompt(t("reject_reason_prompt") || "Motivazione (opzionale):", "");
    try { await api.post(`/events/${id}/reject`, { reason: reason || "" }); toast.success(t("rejected")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("generic_error")); }
  };
  const evConclude = async (id) => {
    try { await api.post(`/events/${id}/conclude`); toast.success(t("concluded") || "Concluso"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("generic_error")); }
  };
  const evArchive = async (id) => {
    try { await api.patch(`/events/${id}`, { state: "archived" }); toast.success(t("archived") || "Archiviato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("generic_error")); }
  };
  const evReopen = async (id) => {
    try { await api.patch(`/events/${id}`, { state: "published" }); toast.success(t("reopened") || "Riaperto"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("generic_error")); }
  };
  const evSaveEdit = async () => {
    if (!evEditModal) return;
    try {
      const patch = {
        title: evEditModal.title, description: evEditModal.description,
        what_to_do: evEditModal.what_to_do,
      };
      await api.patch(`/events/${evEditModal.event_id}`, patch);
      toast.success(t("saved") || "Salvato");
      setEvEditModal(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("generic_error"));
    }
  };
  const evDelete = async (id) => {
    if (!window.confirm(t("delete_event_confirm") || "Eliminare definitivamente questo evento?")) return;
    try { await api.delete(`/events/${id}`); toast.success(t("deleted") || "Eliminato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("generic_error")); }
  };

  const EVENT_STATE_BUCKETS = [
    { k: "pending_moderation", labelKey: "st_pending_moderation" },
    { k: "published", labelKey: "st_published" },
    { k: "concluded", labelKey: "st_concluded" },
    { k: "archived", labelKey: "st_archived" },
    { k: "rejected", labelKey: "st_rejected" },
  ];

  const EventsSection = () => (
    <div data-testid="admin-events-section">
      <SectionHeader onBack={() => { setSection("home"); clearEventSelection(); }} title={t("admin_tab_events")} count={filteredEvents.length} />
      <div className="mt-4 flex flex-wrap gap-2" data-testid="events-buckets">
        {EVENT_STATE_BUCKETS.map((b) => (
          <button key={b.k} data-testid={`events-bucket-${b.k}`}
            onClick={() => toggleBucket(evFilterState, b.k, setEvFilterState)}
            className={`h-8 px-3 rounded-full text-xs font-semibold border-2 ${evFilterState.includes(b.k) ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
            {t(b.labelKey)}
          </button>
        ))}
        {evFilterState.length > 0 && (
          <button data-testid="events-bucket-clear" onClick={() => setEvFilterState([])} className="h-8 px-3 rounded-full text-xs text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1"><RotateCcw size={11} /> {t("reset")}</button>
        )}
      </div>

      {/* Bulk toolbar */}
      {visibleEventIds.length > 0 && (
        <div className="mt-3 sticky top-0 z-10 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-2xl px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="bulk-events-toolbar">
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
            <input type="checkbox" data-testid="bulk-events-select-all"
              checked={allVisibleEventsSelected} onChange={toggleSelectAllEvents}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
            {t("bulk_select_all")}
          </label>
          <span className="text-xs text-slate-500 tabular-nums" data-testid="bulk-events-count">
            {t("bulk_selected_of").replace("{n}", String(selectedEvents.size)).replace("{total}", String(visibleEventIds.length))}
          </span>
          {selectedEvents.size > 0 && (
            <>
              <div className="mx-1 h-4 w-px bg-slate-200" />
              <Button size="sm" data-testid="bulk-events-approve" disabled={evBulkBusy} onClick={() => evBulkAction("approve")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white text-xs h-8">
                <CheckCircle2 size={13} className="mr-1" /> {t("approve")}
              </Button>
              <Button size="sm" variant="outline" data-testid="bulk-events-reject" disabled={evBulkBusy} onClick={() => evBulkAction("reject")} className="rounded-full text-xs h-8 hover:bg-red-50 hover:text-red-800">
                <XCircle size={13} className="mr-1" /> {t("reject")}
              </Button>
              <Button size="sm" variant="outline" data-testid="bulk-events-archive" disabled={evBulkBusy} onClick={() => evBulkAction("archive")} className="rounded-full text-xs h-8 hover:bg-slate-900 hover:text-white">
                {t("archive")}
              </Button>
              {user.role === "admin" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="bulk-events-delete" disabled={evBulkBusy} className="rounded-full text-xs h-8 hover:bg-red-50 hover:text-red-800 text-red-700 border-red-200">
                      <Trash2 size={13} className="mr-1" /> {t("delete")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="bulk-events-delete-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("bulk_delete_confirm_title")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("bulk_delete_confirm_body").replace("{n}", String(selectedEvents.size))}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="bulk-events-delete-cancel">{t("cancel")}</AlertDialogCancel>
                      <AlertDialogAction data-testid="bulk-events-delete-confirm" onClick={evBulkDelete} className="bg-red-600 hover:bg-red-700 text-white">
                        {t("delete")} ({selectedEvents.size})
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <button data-testid="bulk-events-clear" onClick={clearEventSelection} className="text-xs text-slate-500 hover:text-slate-900 px-2 h-8 rounded-full hover:bg-slate-100 inline-flex items-center gap-1">
                <X size={12} /> {t("bulk_clear_selection")}
              </button>
            </>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4">
        {filteredEvents.length === 0 ? (
          <div className="p-8 text-center text-slate-500">{t("no_events") || "Nessun evento straordinario"}</div>
        ) : filteredEvents.map((ev) => {
          const st = ev.state;
          const stColor = st === "published" ? "bg-emerald-100 text-emerald-800"
            : st === "pending_moderation" ? "bg-yellow-100 text-yellow-800"
            : st === "concluded" ? "bg-slate-200 text-slate-700"
            : st === "archived" ? "bg-slate-100 text-slate-600"
            : st === "rejected" ? "bg-red-100 text-red-800"
            : "bg-slate-100 text-slate-700";
          const stLabel = t(`st_${st}`) || st;
          const isAdmin = user.role === "admin";
          const isMod = isAdmin || user.role === "moderator";
          const isSelected = selectedEvents.has(ev.event_id);
          return (
            <div key={ev.event_id} className={`p-4 border-t border-slate-100 first:border-t-0 ${isSelected ? "bg-emerald-50/40" : ""}`} data-testid={`event-row-${ev.event_id}`}>
              <div className="flex items-start gap-3">
                <label className="pt-1 shrink-0 cursor-pointer">
                  <input type="checkbox" data-testid={`event-select-${ev.event_id}`}
                    checked={isSelected} onChange={() => toggleEventSelected(ev.event_id)}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                </label>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-orange-600 text-white border-0 uppercase tracking-widest text-[10px] font-semibold">event</Badge>
                    <Badge className={`${stColor} border-0`}>{stLabel}</Badge>
                    <Badge variant="outline">{ev.kind}</Badge>
                    {ev.author_verified_entity && <Badge className="bg-blue-100 text-blue-800 border-0">{t("verified_entity") || "ente verificato"}</Badge>}
                    <div className="text-[11px] text-slate-500 ml-auto">{new Date(ev.created_at).toLocaleString()}</div>
                  </div>
                  <div className="font-heading font-semibold text-slate-900 mt-2 break-words">{ev.title}</div>
                  {ev.description && <div className="text-xs text-slate-600 mt-0.5 break-words line-clamp-2">{ev.description}</div>}
                  {ev.what_to_do && (
                    <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-2 text-[11px] text-red-800">
                      <b className="uppercase tracking-widest">{t("what_to_do_now")}:</b>{" "}
                      <span className="whitespace-pre-line">{ev.what_to_do}</span>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => nav(`/event/${ev.event_id}`)} data-testid={`event-open-${ev.event_id}`} className="rounded-full h-8 text-xs hover:bg-slate-900 hover:text-white">{t("open") || "Apri"}</Button>
                    {isMod && (
                      <Button size="sm" variant="outline" onClick={() => setEvEditModal({ ...ev })} data-testid={`event-edit-${ev.event_id}`} className="rounded-full h-8 text-xs hover:bg-slate-100">
                        {t("edit") || "Modifica"}
                      </Button>
                    )}
                    {isMod && st === "pending_moderation" && (
                      <>
                        <Button size="sm" onClick={() => evApprove(ev.event_id)} data-testid={`event-approve-${ev.event_id}`} className="rounded-full h-8 bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white text-xs">{t("approve")}</Button>
                        <Button size="sm" variant="outline" onClick={() => evReject(ev.event_id)} data-testid={`event-reject-${ev.event_id}`} className="rounded-full h-8 text-xs hover:bg-red-50 hover:text-red-800">{t("reject")}</Button>
                      </>
                    )}
                    {isMod && st === "published" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => evConclude(ev.event_id)} data-testid={`event-conclude-${ev.event_id}`} className="rounded-full h-8 text-xs hover:bg-slate-900 hover:text-white">{t("conclude") || "Concludi"}</Button>
                        <Button size="sm" variant="outline" onClick={() => evArchive(ev.event_id)} data-testid={`event-archive-${ev.event_id}`} className="rounded-full h-8 text-xs hover:bg-slate-100">{t("archive")}</Button>
                      </>
                    )}
                    {isMod && (st === "concluded" || st === "archived" || st === "rejected") && (
                      <Button size="sm" variant="outline" onClick={() => evReopen(ev.event_id)} data-testid={`event-reopen-${ev.event_id}`} className="rounded-full h-8 text-xs hover:bg-emerald-50 hover:text-emerald-800">
                        <RotateCcw size={12} className="mr-1" /> {t("reopen") || "Riapri"}
                      </Button>
                    )}
                    {isAdmin && (
                      <Button size="sm" variant="outline" onClick={() => evDelete(ev.event_id)} data-testid={`event-delete-${ev.event_id}`} className="rounded-full h-8 text-xs hover:bg-red-50 hover:text-red-800 text-red-700 border-red-200"><Trash2 size={12} className="mr-1" /> {t("delete")}</Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Edit dialog */}
      {evEditModal && (
        <Dialog open={!!evEditModal} onOpenChange={(o) => !o && setEvEditModal(null)}>
          <DialogContent data-testid="event-edit-dialog">
            <DialogHeader><DialogTitle>{t("edit") || "Modifica"} — {evEditModal.title}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("title")}</div>
                <Input data-testid="event-edit-title" value={evEditModal.title || ""} onChange={(e) => setEvEditModal({ ...evEditModal, title: e.target.value })} className="rounded-xl" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("description") || "Descrizione"}</div>
                <Textarea data-testid="event-edit-description" value={evEditModal.description || ""} onChange={(e) => setEvEditModal({ ...evEditModal, description: e.target.value })} className="rounded-xl" rows={3} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("what_to_do_now")}</div>
                <Textarea data-testid="event-edit-what-to-do" value={evEditModal.what_to_do || ""} onChange={(e) => setEvEditModal({ ...evEditModal, what_to_do: e.target.value })} className="rounded-xl" rows={3} placeholder={t("event_whattodo_placeholder") || "Istruzioni per cittadini..."} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEvEditModal(null)} className="rounded-full hover:bg-slate-100">{t("cancel")}</Button>
              <Button data-testid="event-edit-save" onClick={evSaveEdit} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("save") || "Salva"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );


  // Lotto 12 — user search filtering. Plain function (called on each render)
  // to sidestep eslint rules-of-hooks false positive when placed between two
  // arrow-function components declared inside the same body. Cost is trivial
  // for the admin users list.
  // Lotto 13 — computeFilteredUsers rimosso: la logica è ora dentro il
  // componente stabile `AdminUsersSection` in fondo al file.

  // ---- MARKETPLACE SECTION (Lotto 11) ----
  // Riusa la stessa logica gestionale delle Verdi/Gialle/Rosse:
  // SectionHeader + tipo chip + state buckets + BulkToolbar + checkbox riga.
  const MK_STATE_BUCKETS = [
    { k: "published", labelKey: "mk_st_published" },
    { k: "archived", labelKey: "mk_st_archived" },
    { k: "closed", labelKey: "mk_st_closed" },
    { k: "pending_confirmation", labelKey: "mk_st_pending" },
  ];
  const MK_TYPES = [
    { k: "sale", labelKey: "mk_type_sale" },
    { k: "gift", labelKey: "mk_type_gift" },
    { k: "reusable", labelKey: "mk_type_reusable" },
  ];

  const MarketplaceSection = () => (
    <div data-testid="admin-marketplace-section">
      <SectionHeader onBack={() => { setSection("home"); clearListingSelection(); }}
        title={t("admin_card_marketplace") || "Marketplace"} count={filteredListings.length} />

      {/* Type chip selector — same visual pattern as StatusBuckets */}
      <div className="flex flex-wrap gap-2" data-testid="mk-type-buckets">
        {MK_TYPES.map((tp) => (
          <button key={tp.k} data-testid={`mk-type-${tp.k}`}
            onClick={() => { setMkFilterType(tp.k); clearListingSelection(); }}
            className={`h-8 px-3 rounded-full text-xs font-semibold border-2 ${mkFilterType === tp.k ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
            {t(tp.labelKey)}
          </button>
        ))}
      </div>

      {/* State buckets (multi-select, same visual pattern as reports) */}
      <div className="mt-3 flex flex-wrap gap-2" data-testid="mk-state-buckets">
        {MK_STATE_BUCKETS.map((b) => (
          <button key={b.k} data-testid={`mk-bucket-${b.k}`}
            onClick={() => toggleBucket(mkFilterState, b.k, setMkFilterState)}
            className={`h-8 px-3 rounded-full text-xs font-semibold border-2 ${mkFilterState.includes(b.k) ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
            {t(b.labelKey)}
          </button>
        ))}
        {mkFilterState.length > 0 && (
          <button data-testid="mk-bucket-clear" onClick={() => setMkFilterState([])} className="h-8 px-3 rounded-full text-xs text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1"><RotateCcw size={11} /> {t("reset")}</button>
        )}
      </div>

      {/* Search */}
      <div className="mt-3 flex items-center gap-2 bg-white border border-slate-200 rounded-2xl px-3 py-2" data-testid="mk-search-bar">
        <Search size={14} className="text-slate-400" />
        <Input data-testid="mk-search" value={mkSearchQ} onChange={(e) => setMkSearchQ(e.target.value)} placeholder={t("mk_search_placeholder") || "Cerca titolo, autore, provincia…"} className="h-8 border-0 focus-visible:ring-0 flex-1" />
        {mkSearchQ && <button data-testid="mk-search-clear" onClick={() => setMkSearchQ("")} className="text-slate-400 hover:text-slate-900"><X size={14} /></button>}
      </div>

      {/* Bulk toolbar — identical pattern to reports/events/photos */}
      {visibleListingIds.length > 0 && (
        <div className="mt-3 sticky top-0 z-10 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-2xl px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="bulk-mk-toolbar">
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
            <input type="checkbox" data-testid="bulk-mk-select-all"
              checked={allVisibleListingsSelected} onChange={toggleSelectAllListings}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
            {t("bulk_select_all")}
          </label>
          <span className="text-xs text-slate-500 tabular-nums" data-testid="bulk-mk-count">
            {t("bulk_selected_of").replace("{n}", String(selectedListings.size)).replace("{total}", String(visibleListingIds.length))}
          </span>
          {selectedListings.size > 0 && (
            <>
              <div className="mx-1 h-4 w-px bg-slate-200" />
              <Button size="sm" variant="outline" data-testid="bulk-mk-archive" disabled={mkBulkBusy} onClick={() => mkBulkAction("archive")} className="rounded-full text-xs h-8 hover:bg-slate-900 hover:text-white">
                {t("archive")}
              </Button>
              <Button size="sm" data-testid="bulk-mk-reactivate" disabled={mkBulkBusy} onClick={() => mkBulkAction("reactivate")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8">
                <RotateCcw size={13} className="mr-1" /> {t("reactivate") || "Riattiva"}
              </Button>
              {user.role === "admin" && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="bulk-mk-delete" disabled={mkBulkBusy} className="rounded-full text-xs h-8 hover:bg-red-50 hover:text-red-800 text-red-700 border-red-200">
                      <Trash2 size={13} className="mr-1" /> {t("delete")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="bulk-mk-delete-dialog">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("bulk_delete_confirm_title")}</AlertDialogTitle>
                      <AlertDialogDescription>{t("bulk_delete_confirm_body").replace("{n}", String(selectedListings.size))}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="bulk-mk-delete-cancel">{t("cancel")}</AlertDialogCancel>
                      <AlertDialogAction data-testid="bulk-mk-delete-confirm" onClick={() => mkBulkAction("delete")} className="bg-red-600 hover:bg-red-700 text-white">
                        {t("delete")} ({selectedListings.size})
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <button data-testid="bulk-mk-clear" onClick={clearListingSelection} className="text-xs text-slate-500 hover:text-slate-900 px-2 h-8 rounded-full hover:bg-slate-100 inline-flex items-center gap-1">
                <X size={12} /> {t("bulk_clear_selection")}
              </button>
            </>
          )}
        </div>
      )}

      {/* Listing rows */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4">
        {filteredListings.length === 0 ? (
          <div className="p-8 text-center text-slate-500">{t("mk_empty") || "Nessun annuncio in questa vista"}</div>
        ) : filteredListings.map((l) => {
          const isAdmin = user.role === "admin";
          const isSelected = selectedListings.has(l.listing_id);
          const st = l.state || "published";
          const stColor = st === "published" ? "bg-emerald-100 text-emerald-800"
            : st === "archived" ? "bg-slate-200 text-slate-700"
            : st === "closed" ? "bg-slate-100 text-slate-600"
            : st === "pending_confirmation" ? "bg-yellow-100 text-yellow-800"
            : "bg-slate-100 text-slate-700";
          return (
            <div key={l.listing_id} className={`p-4 border-t border-slate-100 first:border-t-0 ${isSelected ? "bg-emerald-50/40" : ""}`} data-testid={`mk-row-${l.listing_id}`}>
              <div className="flex items-start gap-3">
                <label className="pt-1 shrink-0 cursor-pointer">
                  <input type="checkbox" data-testid={`mk-select-${l.listing_id}`}
                    checked={isSelected} onChange={() => toggleListingSelected(l.listing_id)}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                </label>
                {l.photos?.[0] && (
                  <FullscreenMedia path={l.photos[0]} testid={`mk-photo-fs-${l.listing_id}`}>
                    <button className="w-20 h-20 rounded-xl overflow-hidden shrink-0 border border-slate-200"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${l.photos[0]}`} alt="" className="w-full h-full object-cover" /></button>
                  </FullscreenMedia>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="uppercase text-[10px]" style={{ background: l.type === "sale" ? "#A78BFA" : l.type === "gift" ? "#60A5FA" : "#C08E5C", color: "#fff" }}>{t(`mk_type_${l.type}`) || l.type}</Badge>
                    <Badge className={`${stColor} border-0`}>{t(`mk_st_${st}`) || st}</Badge>
                    <Badge variant="outline">{l.category}</Badge>
                    {typeof l.price === "number" && <Badge variant="outline">€ {l.price.toFixed(2)}</Badge>}
                    {l.type === "reusable" && (typeof l.found_present_count === "number") && (
                      <>
                        <Badge className="bg-emerald-50 text-emerald-800 border-0" data-testid={`mk-found-present-${l.listing_id}`}>{t("mk_found_present_short") || "Presente"} · {l.found_present_count}</Badge>
                        <Badge className="bg-red-50 text-red-800 border-0" data-testid={`mk-found-not-present-${l.listing_id}`}>{t("mk_found_not_present_short") || "Non presente"} · {l.found_not_present_count}</Badge>
                      </>
                    )}
                    <div className="text-[11px] text-slate-500 ml-auto">{new Date(l.created_at).toLocaleString()}</div>
                  </div>
                  <div className="font-heading font-semibold text-slate-900 mt-2 break-words">{l.title}</div>
                  {l.description && <div className="text-xs text-slate-600 mt-0.5 break-words line-clamp-2">{l.description}</div>}
                  <div className="text-[11px] text-slate-500 mt-1">{t("author_line_by")} {l.owner_name || "—"}{l.province_name ? ` · ${l.province_name}` : ""}</div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => nav(`/marketplace/${l.listing_id}`)} data-testid={`mk-open-${l.listing_id}`} className="rounded-full h-8 text-xs hover:bg-slate-900 hover:text-white">{t("open") || "Apri"}</Button>
                    <Button size="sm" variant="outline" onClick={() => openListingDetail(l)} data-testid={`mk-detail-${l.listing_id}`} className="rounded-full h-8 text-xs hover:bg-slate-100">{t("mk_admin_details") || "Dettagli admin"}</Button>
                    {st !== "archived" && (
                      <Button size="sm" variant="outline" onClick={() => mkAction(l.listing_id, "archive")} data-testid={`mk-archive-${l.listing_id}`} className="rounded-full h-8 text-xs hover:bg-slate-900 hover:text-white">{t("archive")}</Button>
                    )}
                    {(st === "archived" || st === "closed") && (
                      <Button size="sm" onClick={() => mkAction(l.listing_id, "reactivate")} data-testid={`mk-reactivate-${l.listing_id}`} className="rounded-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                        <RotateCcw size={12} className="mr-1" /> {t("reactivate") || "Riattiva"}
                      </Button>
                    )}
                    {isAdmin && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" data-testid={`mk-delete-${l.listing_id}`} className="rounded-full h-8 text-xs hover:bg-red-50 hover:text-red-800 text-red-700 border-red-200"><Trash2 size={12} className="mr-1" /> {t("delete")}</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t("delete_listing_title") || "Elimina annuncio"}</AlertDialogTitle>
                            <AlertDialogDescription>{t("delete_listing_desc") || "L'annuncio verrà eliminato definitivamente. L'operazione non è reversibile."}</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`mk-delete-cancel-${l.listing_id}`}>{t("cancel")}</AlertDialogCancel>
                            <AlertDialogAction data-testid={`mk-delete-confirm-${l.listing_id}`} onClick={() => mkAction(l.listing_id, "delete")} className="bg-red-600 hover:bg-red-700 text-white">{t("delete")}</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail modal — conferme utente + info admin */}
      {listingDetail && (
        <Dialog open={!!listingDetail} onOpenChange={(o) => !o && setListingDetail(null)}>
          <DialogContent data-testid="mk-detail-dialog" className="max-w-2xl">
            <DialogHeader><DialogTitle>{listingDetail.listing?.title}</DialogTitle></DialogHeader>
            <div className="space-y-3 text-sm">
              <div><b>ID:</b> <span className="font-mono text-xs">{listingDetail.listing?.listing_id}</span></div>
              <div><b>Autore:</b> {listingDetail.listing?.owner_name || "—"}</div>
              <div><b>Stato:</b> {t(`mk_st_${listingDetail.listing?.state}`) || listingDetail.listing?.state}</div>
              <div><b>Categoria:</b> {listingDetail.listing?.category} · {listingDetail.listing?.subcategory}</div>
              <div><b>Provincia:</b> {listingDetail.listing?.province_name || "—"}</div>
              <div><b>Creato:</b> {listingDetail.listing?.created_at ? new Date(listingDetail.listing.created_at).toLocaleString() : "—"}</div>
              {listingDetail.listing?.type === "reusable" && (
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{t("mk_found_checks_title") || "Conferme utenti"}</div>
                  <div className="flex gap-2 mb-3">
                    <Badge className="bg-emerald-50 text-emerald-800 border-0" data-testid="mk-detail-present-count">{t("mk_found_present") || "Oggetto presente"} · {(listingDetail.checksData?.present_count) || 0}</Badge>
                    <Badge className="bg-red-50 text-red-800 border-0" data-testid="mk-detail-not-present-count">{t("mk_found_not_present") || "Oggetto non presente"} · {(listingDetail.checksData?.not_present_count) || 0}</Badge>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-2" data-testid="mk-detail-checks-list">
                    {((listingDetail.checksData?.checks) || []).length === 0 ? (
                      <div className="text-xs text-slate-500">{t("mk_found_no_checks") || "Nessuna conferma finora."}</div>
                    ) : (listingDetail.checksData.checks || []).slice().reverse().map((c) => (
                      <div key={c.check_id} className="text-xs border-b border-slate-100 pb-1.5" data-testid={`mk-check-${c.check_id}`}>
                        <span className={`inline-block px-2 py-0.5 rounded-full font-semibold ${c.present ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                          {c.present ? (t("mk_found_present") || "Oggetto presente") : (t("mk_found_not_present") || "Oggetto non presente")}
                        </span>
                        <span className="ml-2 text-slate-500">{new Date(c.at).toLocaleString()}</span>
                        {c.user_name && <span className="ml-2 text-slate-700">· {c.user_name}</span>}
                        {c.note && <div className="mt-1 text-slate-600 italic">"{c.note}"</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setListingDetail(null)} className="rounded-full">{t("close") || "Chiudi"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );



  // Lotto 12/13 — La UsersSection ora è un componente STABILE definito fuori
  // dalla funzione parent (in fondo al file). Il render inline qui passa solo
  // le props necessarie: React NON rimonta più l'input di ricerca ad ogni
  // keystroke, quindi il focus/tastiera mobile restano stabili.

  const DonationsSection = () => (
    <div data-testid="admin-donations-section">
      <SectionHeader onBack={() => setSection("others")} title={t("admin_tab_donations")} count={donations.length} />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4">
        {donations.length === 0 && <div className="p-8 text-center text-slate-500">{t("no_donations")}</div>}
        {donations.map((d, i) => (
          <div key={d.session_id} className={`p-4 flex flex-wrap items-center gap-3 ${i > 0 ? "border-t border-slate-100" : ""}`} data-testid={`donation-row-${d.session_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-slate-900">€{d.amount?.toFixed(2)} <span className="text-xs font-medium text-slate-500 uppercase">{d.currency}</span></div>
              <div className="text-xs text-slate-500">{d.target_type}{d.target_id ? ` · ${d.target_id}` : ""} · {new Date(d.created_at).toLocaleString()}</div>
              {d.message && <div className="text-xs text-slate-600 mt-1 italic max-w-lg">"{d.message}"</div>}
            </div>
            <Badge className={d.payment_status === "paid" ? "bg-emerald-100 text-emerald-800 border-0" : "bg-slate-100 text-slate-700 border-0"}>{d.payment_status}</Badge>
          </div>
        ))}
      </div>
    </div>
  );

  const LogsSection = () => (
    <div data-testid="admin-logs-section">
      <SectionHeader onBack={() => setSection("others")} title={t("admin_tab_logs")} count={logs.length} />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4">
        {logs.length === 0 && <div className="p-8 text-center text-slate-500">{t("no_logs")}</div>}
        {logs.map((l, i) => (
          <div key={i} className="p-3 border-t border-slate-100 first:border-t-0 text-sm text-slate-800 flex flex-wrap gap-2 items-center" data-testid={`log-row-${i}`}>
            <span className="capitalize font-mono text-xs text-emerald-700">{t(`st_${l.status}`) || l.status?.replace(/_/g," ")}</span>
            <span className="text-xs text-slate-500">{l.report_id}</span>
            <span className="text-xs text-slate-500">{new Date(l.at).toLocaleString()}</span>
            {l.note && <span className="text-xs text-slate-600 italic">"{l.note}"</span>}
          </div>
        ))}
      </div>
    </div>
  );

  const FeedbackSection = () => (
    <div data-testid="admin-feedback-section">
      <SectionHeader onBack={() => setSection("others")} title={t("admin_tab_feedback")} count={feedback.length} />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-4">
        {feedback.length === 0 && <div className="p-8 text-center text-slate-500">—</div>}
        {feedback.map((f, i) => (
          <div key={f.feedback_id || i} className="p-4 border-t border-slate-100 first:border-t-0" data-testid={`fb-row-${f.feedback_id || i}`}>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{f.kind}</div>
            <div className="text-sm text-slate-900 mt-1 break-words">{f.message}</div>
            <div className="text-[10px] text-slate-400 mt-1">{f.user_name || f.email || "—"} · {new Date(f.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );

  const VerifiedEntitiesSection = () => {
    const [list, setList] = useState([]);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const loadList = async () => {
      try { const { data } = await api.get("/admin/verified-entities"); setList(data || []); } catch {}
    };
    useEffect(() => { loadList(); }, []);
    const search = async () => {
      if (!q.trim()) return setResults([]);
      try { const { data } = await api.get("/admin/users/search", { params: { q: q.trim() } }); setResults(data || []); } catch {}
    };
    const promote = async (u, kind, reason) => {
      setBusyId(u.user_id);
      try {
        await api.post(`/admin/users/${u.user_id}/verified-entity`, { enabled: true, kind, reason });
        toast.success("Ente verificato assegnato");
        loadList(); search();
      } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
      finally { setBusyId(null); }
    };
    const revoke = async (u, reason) => {
      if (!window.confirm(`Revocare il ruolo verified_entity a ${u.name || u.email}?`)) return;
      setBusyId(u.user_id);
      try {
        await api.post(`/admin/users/${u.user_id}/verified-entity`, { enabled: false, reason: reason || null });
        toast.success("Ruolo revocato");
        loadList();
      } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
      finally { setBusyId(null); }
    };
    return (
      <div data-testid="admin-verified-entities-section">
        <SectionHeader onBack={() => setSection("others")} title={t("admin_verified_entities")} count={list.length} />
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mt-4 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Cerca utente da promuovere</div>
          <div className="flex gap-2">
            <input data-testid="ve-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") search(); }} placeholder="Email, nome o user_id" className="flex-1 h-10 rounded-full px-4 border border-slate-200 text-sm" />
            <button data-testid="ve-search-btn" onClick={search} className="h-10 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold">Cerca</button>
          </div>
          {results.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="ve-search-results">
              {results.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-xl p-2 text-sm" data-testid={`ve-result-${u.user_id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{u.name || "(no name)"} <span className="text-xs text-slate-500 font-normal">{u.email}</span></div>
                    <div className="text-[10px] text-slate-500">{u.role} {u.verified_entity && `· già verificato (${u.verified_entity_kind})`}</div>
                  </div>
                  {u.verified_entity ? (
                    <button data-testid={`ve-revoke-${u.user_id}`} onClick={() => revoke(u, prompt("Motivo revoca (facoltativo):") || "")} disabled={busyId === u.user_id} className="h-8 px-3 rounded-full text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50">Revoca</button>
                  ) : (
                    <div className="flex gap-1">
                      {["civil_protection", "fire_dept", "municipality", "other"].map((k) => (
                        <button key={k} data-testid={`ve-grant-${k}-${u.user_id}`} onClick={() => { const r = prompt(`Motivo assegnazione (${k}):`) || ""; promote(u, k, r); }} disabled={busyId === u.user_id} className="h-8 px-3 rounded-full text-xs font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50">{k === "civil_protection" ? "Prot. Civ." : k === "fire_dept" ? "VVF" : k === "municipality" ? "Comune" : "Altro"}</button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-2">
          <div className="p-3 border-b border-slate-100 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Enti verificati attivi</div>
          {list.length === 0 && <div className="p-6 text-center text-sm text-slate-500 italic">Nessuno.</div>}
          {list.map((u) => (
            <div key={u.user_id} className="p-3 border-t border-slate-100 first:border-t-0 flex items-center justify-between gap-2 text-sm" data-testid={`ve-active-${u.user_id}`}>
              <div className="min-w-0">
                <div className="font-semibold truncate">{u.name || "(no name)"} <span className="text-xs text-slate-500 font-normal">{u.email}</span></div>
                <div className="text-[10px] text-slate-500">Tipo: {u.verified_entity_kind} · dal {u.verified_entity_since ? new Date(u.verified_entity_since).toLocaleDateString() : "?"}</div>
              </div>
              <button data-testid={`ve-active-revoke-${u.user_id}`} onClick={() => revoke(u, prompt("Motivo revoca (facoltativo):") || "")} disabled={busyId === u.user_id} className="h-8 px-3 rounded-full text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50">Revoca</button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const AdminManagementSection = () => {
    const [admins, setAdmins] = useState([]);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const loadAdmins = async () => { try { const { data } = await api.get("/admin/admins"); setAdmins(data || []); } catch {} };
    useEffect(() => { loadAdmins(); }, []);
    const search = async () => { if (!q.trim()) return setResults([]); try { const { data } = await api.get("/admin/users/search", { params: { q: q.trim() } }); setResults(data || []); } catch {} };
    const setRole = async (u, role) => {
      const conf = window.confirm(role === "admin" ? `Nominare ${u.name || u.email} come Admin?` : `Revocare Admin a ${u.name || u.email}?`);
      if (!conf) return;
      const reason = prompt("Motivazione (facoltativa):") || null;
      setBusyId(u.user_id);
      try {
        await api.post(`/admin/users/${u.user_id}/role`, { role, reason });
        toast.success(role === "admin" ? "Ruolo Admin assegnato" : "Ruolo Admin revocato");
        loadAdmins(); search();
      } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
      finally { setBusyId(null); }
    };
    return (
      <div data-testid="admin-mgmt-section">
        <SectionHeader onBack={() => setSection("others")} title={t("admin_management")} count={admins.length} />
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mt-4 mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Cerca utente da nominare / revocare</div>
          <div className="flex gap-2">
            <input data-testid="am-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") search(); }} placeholder="Email, nome o user_id" className="flex-1 h-10 rounded-full px-4 border border-slate-200 text-sm" />
            <button data-testid="am-search-btn" onClick={search} className="h-10 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold">Cerca</button>
          </div>
          {results.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="am-search-results">
              {results.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-xl p-2 text-sm" data-testid={`am-result-${u.user_id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{u.name || "(no name)"} <span className="text-xs text-slate-500 font-normal">{u.email}</span></div>
                    <div className="text-[10px] text-slate-500">Ruolo attuale: {u.role}</div>
                  </div>
                  {u.role === "admin" || u.role === "moderator" ? (
                    <button data-testid={`am-revoke-${u.user_id}`} onClick={() => setRole(u, "citizen")} disabled={busyId === u.user_id} className="h-8 px-3 rounded-full text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50">Revoca Admin</button>
                  ) : (
                    <button data-testid={`am-grant-${u.user_id}`} onClick={() => setRole(u, "admin")} disabled={busyId === u.user_id} className="h-8 px-3 rounded-full text-xs font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50">Nomina Admin</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="p-3 border-b border-slate-100 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Admin attivi</div>
          {admins.length === 0 && <div className="p-6 text-center text-sm text-slate-500 italic">Nessun admin trovato.</div>}
          {admins.map((u) => (
            <div key={u.user_id} className="p-3 border-t border-slate-100 first:border-t-0 flex items-center justify-between gap-2 text-sm" data-testid={`am-active-${u.user_id}`}>
              <div className="min-w-0">
                <div className="font-semibold truncate">{u.name || "(no name)"} <span className="text-xs text-slate-500 font-normal">{u.email}</span></div>
                <div className="text-[10px] text-slate-500">Ruolo: {u.role} · dal {u.role_updated_at ? new Date(u.role_updated_at).toLocaleDateString() : (u.created_at ? new Date(u.created_at).toLocaleDateString() : "?")}</div>
              </div>
              <button data-testid={`am-active-revoke-${u.user_id}`} onClick={() => setRole(u, "citizen")} disabled={busyId === u.user_id || u.user_id === user.user_id} className="h-8 px-3 rounded-full text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-40">Revoca</button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const PecConfigSection = () => {
    const [cfg, setCfg] = useState({});
    const [saving, setSaving] = useState(false);
    const load = async () => { try { const { data } = await api.get("/admin/pec/config"); setCfg(data || {}); } catch {} };
    useEffect(() => { load(); }, []);
    const save = async () => {
      setSaving(true);
      try {
        await api.put("/admin/pec/config", cfg);
        toast.success("Configurazione salvata"); load();
      } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
      finally { setSaving(false); }
    };
    return (
      <div data-testid="admin-pec-config-section">
        <SectionHeader onBack={() => setSection("others")} title={t("admin_pec_config")} />
        <div className="bg-white border border-slate-200 rounded-2xl p-4 mt-4 space-y-3">
          {!cfg.is_configured && (
            <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900" data-testid="pec-config-warning">
              {t("admin_pec_locked_msg")}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Mittente PEC</div>
              <input data-testid="pec-from" value={cfg.from_pec || ""} onChange={(e) => setCfg({ ...cfg, from_pec: e.target.value })} className="w-full h-10 rounded-full px-4 border border-slate-200 text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Provider</div>
              <input data-testid="pec-provider" value={cfg.provider || ""} onChange={(e) => setCfg({ ...cfg, provider: e.target.value })} placeholder="Aruba, InfoCert, Legalmail…" className="w-full h-10 rounded-full px-4 border border-slate-200 text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">SMTP host</div>
              <input data-testid="pec-host" value={cfg.smtp_host || ""} onChange={(e) => setCfg({ ...cfg, smtp_host: e.target.value })} className="w-full h-10 rounded-full px-4 border border-slate-200 text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">SMTP port</div>
              <input data-testid="pec-port" type="number" value={cfg.smtp_port || ""} onChange={(e) => setCfg({ ...cfg, smtp_port: Number(e.target.value) })} className="w-full h-10 rounded-full px-4 border border-slate-200 text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">SMTP user</div>
              <input data-testid="pec-user" value={cfg.smtp_user || ""} onChange={(e) => setCfg({ ...cfg, smtp_user: e.target.value })} className="w-full h-10 rounded-full px-4 border border-slate-200 text-sm" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">SMTP password</div>
              <input data-testid="pec-pass" type="password" value={cfg.smtp_password || ""} onChange={(e) => setCfg({ ...cfg, smtp_password: e.target.value })} placeholder="(non mostrata dopo il salvataggio)" className="w-full h-10 rounded-full px-4 border border-slate-200 text-sm" />
            </div>
          </div>
          <button data-testid="pec-config-save" onClick={save} disabled={saving} className="h-10 px-4 rounded-full bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800">{saving ? "…" : "Salva configurazione"}</button>
          <button data-testid="pec-config-verify" onClick={async () => {
            setSaving(true);
            try {
              const { data } = await api.post("/admin/pec/config/verify", {});
              if (data.ok) {
                toast.success(data.message || "PEC configurata correttamente");
              } else {
                toast.error(data.message || "Verifica fallita");
              }
              load();
            } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
            finally { setSaving(false); }
          }} disabled={saving} className="h-10 px-4 rounded-full bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 ml-2">Verifica configurazione</button>
          {cfg.is_configured && <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700" data-testid="pec-configured-badge">✓ PEC configurata</span>}
          <div className="text-xs text-slate-500">
            L'invio PEC reale non è ancora integrato in questa build: anche con configurazione completa, l'app registrerà un tentativo «bloccato — trasporto PEC non ancora integrato» e non simulerà mai una consegna.
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="admin-page">
      <AppHeader />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <BackButton className="mb-4" testid="admin-back-btn" />
        {section === "home" && <Home />}
        {section === "reports" && <ReportsSection />}
        {section === "photos" && <PhotosSection />}
        {section === "urgent" && <UrgentSection />}
        {section === "others" && <OthersSection />}
        {section === "users" && (
          <AdminUsersSection
            users={users}
            t={t}
            onBack={() => setSection("others")}
            setRole={setRole}
            flagUser={flagUser}
          />
        )}
        {section === "donations" && <DonationsSection />}
        {section === "logs" && <LogsSection />}
        {section === "feedback" && <FeedbackSection />}
        {section === "user_reports" && <UserReportsSection />}
        {section === "content_reports" && <ContentReportsSection />}
        {section === "events" && <EventsSection />}
        {section === "marketplace" && <MarketplaceSection />}
        {section === "verified_entities" && <VerifiedEntitiesSection />}
        {section === "admin_mgmt" && <AdminManagementSection />}
        {section === "pec_config" && <PecConfigSection />}
      </div>
    </div>
  );
}

// --- Small helper components ---
function HomeCard({ title, count, secondary, color, icon: Icon, onClick, testid }) {
  return (
    <button
      onClick={onClick} data-testid={testid}
      className="text-left bg-white border-2 border-slate-200 rounded-2xl p-4 sm:p-5 hover:border-emerald-400 transition-all min-h-[110px] flex flex-col justify-between"
      style={{ borderTopColor: color, borderTopWidth: 4 }}
    >
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}20`, color }}>
          <Icon size={18} />
        </div>
        <ChevronRight className="text-slate-300" size={18} />
      </div>
      <div>
        <div className="font-heading text-3xl font-bold text-slate-900 tabular-nums" data-testid={`${testid}-count`}>{count ?? 0}</div>
        <div className="text-xs sm:text-sm uppercase tracking-widest text-slate-500 mt-1 break-words">{title}</div>
        {secondary && <div className="text-[10px] text-slate-400 mt-0.5">{secondary}</div>}
      </div>
    </button>
  );
}

function SectionHeader({ title, onBack, count }) {
  const nav = useNavigate();
  return (
    <div className="flex items-center gap-3 mb-4" data-testid="admin-section-header">
      <button onClick={onBack} className="w-9 h-9 rounded-full border border-slate-200 hover:bg-white flex items-center justify-center" data-testid="section-back"><ChevronRight className="rotate-180" size={16} /></button>
      <h2 className="font-heading text-2xl font-bold text-slate-900">{title}</h2>
      {typeof count === "number" && <span className="text-xs text-slate-500 tabular-nums">({count})</span>}
      <button
        onClick={() => nav("/map")}
        data-testid="back-to-map-btn"
        title="Torna alla mappa"
        className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 hover:text-emerald-900 px-3 py-1.5 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-200"
      >
        Torna alla mappa
      </button>
    </div>
  );
}

function StatusBuckets({ buckets, onToggle, t, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {STATUS_BUCKETS.map((b) => (
        <button key={b.k} data-testid={`bucket-${b.k}`} onClick={() => onToggle(b.k)}
          className={`h-8 px-3 rounded-full text-xs font-semibold border-2 ${buckets.includes(b.k) ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
          {t(b.labelKey)}
        </button>
      ))}
      {buckets.length > 0 && (
        <button data-testid="buckets-clear" onClick={() => onToggle(null) /* handled by parent using reset */} className="hidden" />
      )}
    </div>
  );
}

function PhFilterChips({ title, value, onToggle, options, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button key={o.k} data-testid={`${testid}-${o.k}`} onClick={() => onToggle(o.k)}
            className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border ${value.includes(o.k) ? "bg-slate-900 border-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ColorFilterBar({ colorFilter, toggleColor, onClear, t, inline }) {
  const RISK = [
    { k: "green", label: t("green"), bg: "#10B981" },
    { k: "yellow", label: t("yellow"), bg: "#EAB308" },
    { k: "red", label: t("red"), bg: "#EF4444" },
  ];
  return (
    <div className={`${inline ? "flex-wrap" : "p-3 border-b border-slate-100"} flex items-center gap-2`} data-testid="admin-color-filter">
      {!inline && <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mr-2">{t("color")}</span>}
      {RISK.map((c) => (
        <button
          key={c.k} data-testid={`admin-color-${c.k}`} onClick={() => toggleColor(c.k)}
          className="h-8 px-3 rounded-full text-xs font-semibold border-2 inline-flex items-center gap-1.5"
          style={colorFilter.includes(c.k) ? { background: c.bg, borderColor: c.bg, color: "#fff" } : { borderColor: "#E2E8F0", color: "#334155", background: "#fff" }}
        >
          <span className="w-2 h-2 rounded-full" style={{ background: colorFilter.includes(c.k) ? "#fff" : c.bg }} /> {c.label}
        </button>
      ))}
      {colorFilter.length > 0 && (
        <button data-testid="admin-color-clear" onClick={onClear} className="h-8 px-3 rounded-full text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 inline-flex items-center gap-1"><RotateCcw size={11} /> {t("reset")}</button>
      )}
    </div>
  );
}

function GlobalFilters({ open, setOpen, advOpen, setAdvOpen, searchQ, setSearchQ, colorFilter, toggleColor, filterCategory, setFilterCategory, filterDate, setFilterDate, filterAiConf, setFilterAiConf, filterSensitive, setFilterSensitive, filterLegal, setFilterLegal, onReset, t }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl mt-4" data-testid="admin-global-filters">
      <button onClick={() => setOpen(!open)} className="w-full p-3 flex items-center justify-between text-sm font-semibold text-slate-900" data-testid="filters-toggle">
        <span className="flex items-center gap-2"><Filter size={14} /> {t("filters")}</span>
        <ChevronRight className={`transition-transform ${open ? "rotate-90" : ""}`} size={16} />
      </button>
      {open && (
        <div className="p-3 space-y-3 border-t border-slate-100">
          <div className="flex items-center gap-2">
            <Search size={14} className="text-slate-400" />
            <Input data-testid="filter-search" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder={t("filter_search_placeholder")} className="h-9 rounded-full" />
          </div>
          <ColorFilterBar colorFilter={colorFilter} toggleColor={toggleColor} onClear={() => { /* handled by parent */ toggleColor && colorFilter.forEach((c) => toggleColor(c)); }} t={t} inline />
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-[140px]">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("category")}</div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-9 rounded-lg text-sm" data-testid="filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("admin_st_all")}</SelectItem>
                  {Object.keys(CATS).map((c) => <SelectItem key={c} value={c}>{t(`cat_${c}`) || c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[140px]">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("date")}</div>
              <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="h-9 rounded-lg text-sm" data-testid="filter-date" />
            </div>
          </div>
          <div className="pt-2 border-t border-slate-100">
            <button onClick={() => setAdvOpen(!advOpen)} className="text-xs text-emerald-700 font-semibold flex items-center gap-1" data-testid="filters-adv-toggle">
              {t("advanced_filters")} <ChevronRight size={12} className={`transition-transform ${advOpen ? "rotate-90" : ""}`} />
            </button>
            {advOpen && (
              <div className="mt-2 space-y-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("filter_ai_conf")} ≥ {filterAiConf}%</div>
                  <input type="range" min="0" max="100" value={filterAiConf} onChange={(e) => setFilterAiConf(parseInt(e.target.value))} className="w-full" data-testid="filter-ai-conf" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={filterSensitive} onChange={(e) => setFilterSensitive(e.target.checked)} data-testid="filter-sensitive" /> {t("filter_sensitive")}</label>
                  <label className="text-xs flex items-center gap-2"><input type="checkbox" checked={filterLegal} onChange={(e) => setFilterLegal(e.target.checked)} data-testid="filter-legal" /> {t("filter_legal")}</label>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-2 border-t border-slate-100">
            <button data-testid="filters-reset" onClick={onReset} className="text-xs px-3 h-8 rounded-full border border-slate-200 hover:bg-slate-100 inline-flex items-center gap-1"><RotateCcw size={11} /> {t("reset")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewReasonsBadges({ reasons, t }) {
  if (!reasons || reasons.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1" data-testid="review-reasons">
      {reasons.map((r) => (
        <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold uppercase tracking-widest" data-testid={`reason-${r}`}>
          {t(`reason_${r}`) || r.replace(/_/g," ")}
        </span>
      ))}
    </div>
  );
}

// Bulk selection toolbar for reports (Lotto 3 #7). Shows how many rows are
// selected, a "Seleziona tutti"/"Deseleziona" toggle, and the three admin
// bulk actions (Approva / Rifiuta / Archivia + destructive Elimina). The
// destructive delete goes through an AlertDialog with an explicit count.
function BulkReportsToolbar({ t, user, totalVisible, selectedCount, allSelected, onToggleAll, onClear, onBulkDelete, onBulkSetStatus, busy }) {
  const canDelete = user?.role === "admin";
  const canModerate = user?.role === "admin" || user?.role === "moderator";
  return (
    <div className="mt-3 sticky top-0 z-10 bg-white/90 backdrop-blur-sm border border-slate-200 rounded-2xl px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="bulk-reports-toolbar">
      <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
        <input
          type="checkbox"
          data-testid="bulk-reports-select-all"
          checked={allSelected}
          onChange={onToggleAll}
          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
        />
        {t("bulk_select_all")}
      </label>
      <span className="text-xs text-slate-500 tabular-nums" data-testid="bulk-reports-count">
        {t("bulk_selected_of").replace("{n}", String(selectedCount)).replace("{total}", String(totalVisible))}
      </span>
      {selectedCount > 0 && (
        <>
          <div className="mx-1 h-4 w-px bg-slate-200" />
          {canModerate && (
            <>
              <Button size="sm" data-testid="bulk-reports-approve" disabled={busy} onClick={() => onBulkSetStatus("published")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white text-xs h-8">
                <CheckCircle2 size={13} className="mr-1" /> {t("approve")}
              </Button>
              <Button size="sm" variant="outline" data-testid="bulk-reports-reject" disabled={busy} onClick={() => onBulkSetStatus("rejected")} className="rounded-full text-xs h-8 hover:bg-red-50 hover:text-red-800">
                <XCircle size={13} className="mr-1" /> {t("reject")}
              </Button>
              <Button size="sm" variant="outline" data-testid="bulk-reports-archive" disabled={busy} onClick={() => onBulkSetStatus("archived")} className="rounded-full text-xs h-8 hover:bg-slate-900 hover:text-white">
                {t("archive")}
              </Button>
            </>
          )}
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="bulk-reports-delete" disabled={busy} className="rounded-full text-xs h-8 hover:bg-red-50 hover:text-red-800 text-red-700 border-red-200">
                  <Trash2 size={13} className="mr-1" /> {t("delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="bulk-reports-delete-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("bulk_delete_confirm_title")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("bulk_delete_confirm_body").replace("{n}", String(selectedCount))}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="bulk-reports-delete-cancel">{t("cancel")}</AlertDialogCancel>
                  <AlertDialogAction data-testid="bulk-reports-delete-confirm" onClick={onBulkDelete} className="bg-red-600 hover:bg-red-700 text-white">
                    {t("delete")} ({selectedCount})
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <button data-testid="bulk-reports-clear" onClick={onClear} className="text-xs text-slate-500 hover:text-slate-900 px-2 h-8 rounded-full hover:bg-slate-100 inline-flex items-center gap-1">
            <X size={12} /> {t("bulk_clear_selection")}
          </button>
        </>
      )}
    </div>
  );
}

function PhotoReviewRow({ p, t, user, selected, onToggleSelected, onAction, busy }) {
  const [editorOpen, setEditorOpen] = useState(false);
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);

  // Prefer the private ORIGINAL only if it's still available in storage (backend gives us the flag).
  // Otherwise fall back to the currently-shown (possibly already-blurred) path so the admin can
  // still apply additional blur on top and re-approve. Admin is warned via a banner.
  const trackedOriginal = (p.face_moderation && p.face_moderation.original) || null;
  const useOriginal = !!trackedOriginal && p.original_available !== false;
  const sourcePath = useOriginal ? trackedOriginal : p.path;
  const originalMissing = !!trackedOriginal && p.original_available === false;
  const isAdmin = user?.role === "admin";
  const canModerate = isAdmin || user?.role === "moderator";
  // La UX di "pubblica come Verde/Gialla/Rossa" è disponibile solo quando la
  // riga è ancora in attesa di moderazione e proviene da un report (source
  // 'new_report'). Per foto aggiuntive il colore è già ereditato dal report
  // parent, quindi Approve mantiene il colore corrente.
  const canPublishAsColor = canModerate && p.source === "new_report" && p.status === "pending";
  const isPending = p.status === "pending";

  const swapTarget = () => {
    if (p.source === "added_photo") return { report_id: p.origin_id, additional_photo_id: p.photo_id };
    if (p.source === "new_report") {
      const idx = parseInt((p.photo_id || "").split("#")[1] || "0", 10);
      return { report_id: p.origin_id, photo_index: idx };
    }
    if (p.source === "urgent") {
      const idx = parseInt((p.photo_id || "").split("#")[1] || "0", 10);
      return { urgent_alert_id: p.origin_id, photo_index: idx };
    }
    if (p.source === "extension") return { urgent_alert_id: p.origin_id, extension_id: p.photo_id };
    return {};
  };

  const downloadOriginal = async () => {
    if (!useOriginal) { toast.error(t("original_unavailable") || "Originale non più disponibile"); return; }
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/photos/original?path=${encodeURIComponent(trackedOriginal)}`;
      const tok = localStorage.getItem("tc_token") || sessionStorage.getItem("tc_token");
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = (trackedOriginal || "photo").split("/").pop();
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error(t("generic_error") || "Errore");
    }
  };

  const uploadReplacement = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const up = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const newPath = up.data.storage_path || up.data.path;
      await api.post("/admin/photos/replace", { new_path: newPath, ...swapTarget() });
      toast.success(t("replacement_uploaded"));
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setUploading(false); }
  };

  return (
    <div className={`p-4 border-t border-slate-100 first:border-t-0 flex flex-col md:flex-row gap-4 ${selected ? "bg-emerald-50/40" : ""}`} data-testid={`ph-row-${p.origin_id}-${p.photo_id}`}>
      {/* Checkbox selezione (identico a report-row) */}
      <label className="pt-1 shrink-0 cursor-pointer md:self-start">
        <input type="checkbox" data-testid={`ph-select-${p.origin_id}-${p.photo_id}`}
          checked={!!selected} onChange={onToggleSelected}
          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
      </label>
      {p.path && (
        <FullscreenMedia path={p.path} testid={`ph-fs-${p.origin_id}`}>
          <button className="w-32 h-32 rounded-xl overflow-hidden shrink-0 border border-slate-200"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p.path}`} alt="" className="w-full h-full object-cover" /></button>
        </FullscreenMedia>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className="uppercase text-[10px]" style={{ background: RISK_COLOR[p.color] || "#9CA3AF", color: "#fff" }}>{t(p.color) || p.color}</Badge>
          <Badge variant="outline">{t(`src_${p.source}`) || p.source}</Badge>
          <Badge variant="outline">{t(`cat_${p.category}`) || p.category}</Badge>
          {p.subcategory && <Badge variant="outline">{t(`sub_${p.subcategory}`) || p.subcategory.replace(/_/g," ")}</Badge>}
          <Badge className={`border-0 ${isPending ? "bg-yellow-100 text-yellow-800" : p.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{t(`admin_st_${isPending ? "pending_approval" : (p.status === "published" ? "approved" : "archived")}`) || p.status}</Badge>
        </div>
        <div className="text-sm text-slate-900 font-semibold mt-2 break-words">{p.description}</div>
        <div className="text-xs text-slate-500 mt-1">{t("author_line_by")} {displayAuthorName(p, t) || "—"} · {p.created_at ? new Date(p.created_at).toLocaleString() : ""}</div>
        {typeof p.confidence === "number" && (
          <div className="text-xs text-slate-600 mt-1">{t("filter_ai_conf")}: <b>{Math.round(p.confidence * 100)}%</b></div>
        )}
        <ReviewReasonsBadges reasons={p.review_reasons} t={t} />
        {originalMissing && (
          <div className="mt-2 text-[11px] px-2.5 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 inline-flex items-center gap-1.5" data-testid={`ph-original-missing-${p.origin_id}-${p.photo_id}`}>
            {t("original_unavailable_hint") || "Originale non più disponibile — puoi comunque applicare un ulteriore oscuramento sulla versione attuale."}
          </div>
        )}

        {/* Riga 1 — Strumenti di gestione foto (blur manuale, originale, sostituzione) */}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button data-testid={`ph-manual-blur-${p.origin_id}-${p.photo_id}`} onClick={() => setEditorOpen(true)} size="sm" variant="outline" className="rounded-full hover:bg-emerald-50 hover:text-emerald-800"><Palette size={14} className="mr-1" /> {t("manual_blur")}</Button>
          {useOriginal && (
            <Button data-testid={`ph-download-orig-${p.origin_id}-${p.photo_id}`} onClick={downloadOriginal} size="sm" variant="outline" className="rounded-full hover:bg-slate-100"><Download size={14} className="mr-1" /> {t("download_original")}</Button>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" data-testid={`ph-replace-input-${p.origin_id}-${p.photo_id}`} onChange={(e) => uploadReplacement(e.target.files?.[0])} />
          <Button data-testid={`ph-upload-replace-${p.origin_id}-${p.photo_id}`} onClick={() => fileInputRef.current?.click()} disabled={uploading} size="sm" variant="outline" className="rounded-full hover:bg-slate-100"><Upload size={14} className="mr-1" /> {uploading ? "…" : t("upload_replacement")}</Button>
          <Link to={p.link} data-testid={`ph-open-${p.origin_id}-${p.photo_id}`} className="text-xs text-emerald-700 hover:underline self-center ml-2">{t("open_details") || "Apri"}</Link>
        </div>

        {/* Riga 2 — Azioni di moderazione (stesso pattern Verdi/Gialle/Rosse) */}
        <div className="mt-2 flex flex-wrap gap-2">
          {/* Pubblica come Verde/Gialla/Rossa (solo per report in pending) */}
          {canPublishAsColor && (
            <>
              <Button data-testid={`ph-publish-green-${p.origin_id}-${p.photo_id}`} disabled={busy} onClick={() => onAction("approve", "green")} size="sm" className="rounded-full h-8 text-white text-xs" style={{ background: RISK_COLOR.green }}>
                <CheckCircle2 size={13} className="mr-1" /> {t("publish_as_green") || "Pubblica come Verde"}
              </Button>
              <Button data-testid={`ph-publish-yellow-${p.origin_id}-${p.photo_id}`} disabled={busy} onClick={() => onAction("approve", "yellow")} size="sm" className="rounded-full h-8 text-white text-xs" style={{ background: RISK_COLOR.yellow }}>
                <CheckCircle2 size={13} className="mr-1" /> {t("publish_as_yellow") || "Pubblica come Gialla"}
              </Button>
              <Button data-testid={`ph-publish-red-${p.origin_id}-${p.photo_id}`} disabled={busy} onClick={() => onAction("approve", "red")} size="sm" className="rounded-full h-8 text-white text-xs" style={{ background: RISK_COLOR.red }}>
                <CheckCircle2 size={13} className="mr-1" /> {t("publish_as_red") || "Pubblica come Rossa"}
              </Button>
            </>
          )}
          {/* Approve semplice (mantiene colore corrente) — per added_photo e altri source */}
          {canModerate && !canPublishAsColor && isPending && (
            <Button data-testid={`ph-approve-${p.origin_id}-${p.photo_id}`} disabled={busy} onClick={() => onAction("approve")} size="sm" className="rounded-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
              <CheckCircle2 size={13} className="mr-1" /> {t("approve")}
            </Button>
          )}
          {canModerate && isPending && (
            <Button data-testid={`ph-reject-${p.origin_id}-${p.photo_id}`} disabled={busy} onClick={() => onAction("reject")} size="sm" variant="outline" className="rounded-full h-8 text-xs hover:bg-red-50 hover:text-red-800">
              <XCircle size={13} className="mr-1" /> {t("reject")}
            </Button>
          )}
          {canModerate && (
            <Button data-testid={`ph-archive-${p.origin_id}-${p.photo_id}`} disabled={busy} onClick={() => onAction("archive")} size="sm" variant="outline" className="rounded-full h-8 text-xs hover:bg-slate-900 hover:text-white">
              {t("archive")}
            </Button>
          )}
          {canModerate && !isPending && (
            <Button data-testid={`ph-reopen-${p.origin_id}-${p.photo_id}`} disabled={busy} onClick={() => onAction("reopen")} size="sm" variant="outline" className="rounded-full h-8 text-xs hover:bg-emerald-50 hover:text-emerald-800">
              <RotateCcw size={13} className="mr-1" /> {t("reopen") || "Riapri"}
            </Button>
          )}
          {isAdmin && p.source === "new_report" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid={`ph-delete-${p.origin_id}-${p.photo_id}`} disabled={busy} size="sm" variant="outline" className="rounded-full h-8 text-xs hover:bg-red-50 hover:text-red-800 text-red-700 border-red-200">
                  <Trash2 size={13} className="mr-1" /> {t("delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid={`ph-delete-dialog-${p.origin_id}-${p.photo_id}`}>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("delete_report_title") || "Elimina segnalazione"}</AlertDialogTitle>
                  <AlertDialogDescription>{t("delete_report_desc") || "L'operazione non è reversibile."}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid={`ph-delete-cancel-${p.origin_id}-${p.photo_id}`}>{t("cancel")}</AlertDialogCancel>
                  <AlertDialogAction data-testid={`ph-delete-confirm-${p.origin_id}-${p.photo_id}`} onClick={() => onAction("delete")} className="bg-red-600 hover:bg-red-700 text-white">{t("delete")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
      {editorOpen && (
        <ManualBlurEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          sourcePath={sourcePath}
          target={swapTarget()}
          onSaved={() => window.location.reload()}
        />
      )}
    </div>
  );
}

function OtherLink({ title, onClick, icon: Icon, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="bg-white border border-slate-200 rounded-2xl p-4 hover:border-emerald-400 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-700"><Icon size={18} /></div>
      <div className="flex-1 text-left text-sm font-semibold text-slate-900">{title}</div>
      <ChevronRight className="text-slate-300" />
    </button>
  );
}

// ---- Full ReportRow (unchanged from previous impl, extended with review reasons) ----
function ReportRow({ r, t, showSensitive, setShowSensitive, user, mod, editReport, decide, deleteReport, reopenReport, selected, onToggleSelected }) {
  return (
    <div className={`p-5 border-t border-slate-100 first:border-t-0 ${selected ? "bg-emerald-50/40" : ""}`} data-testid={`report-row-${r.report_id}`}>
      <div className="flex items-start gap-4">
        {/* Bulk selection checkbox (Lotto 3 #7) */}
        {typeof onToggleSelected === "function" && (
          <label className="pt-1 shrink-0 cursor-pointer" title={t("bulk_select_row")}>
            <input
              type="checkbox"
              data-testid={`report-select-${r.report_id}`}
              checked={!!selected}
              onChange={onToggleSelected}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
          </label>
        )}
        {r.photos?.[0] && !r.contains_sensitive && (
          <FullscreenMedia path={r.photos[0]} testid={`admin-photo-fs-${r.report_id}`}>
            <button className="w-24 h-24 rounded-xl overflow-hidden shrink-0 relative group" data-testid={`admin-photo-${r.report_id}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.photos[0]}`} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100"><Expand size={16} className="text-white" /></div>
            </button>
          </FullscreenMedia>
        )}
        {r.contains_sensitive && (
          <div className="w-24 h-24 rounded-xl bg-slate-900 text-white flex flex-col items-center justify-center shrink-0 relative overflow-hidden" data-testid={`sensitive-${r.report_id}`}>
            {showSensitive[r.report_id] && r.photos?.[0] ? (
              <FullscreenMedia path={r.photos[0]} testid={`admin-sensitive-fs-${r.report_id}`}>
                <button className="w-full h-full"><img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.photos[0]}`} alt="" className="w-full h-full object-cover" /></button>
              </FullscreenMedia>
            ) : (
              <><EyeOff size={20} /><div className="text-[9px] uppercase tracking-widest mt-1 text-center px-1">contenuto sensibile</div></>
            )}
            <button data-testid={`toggle-sensitive-${r.report_id}`} onClick={() => setShowSensitive({ ...showSensitive, [r.report_id]: !showSensitive[r.report_id] })} className="absolute bottom-1 right-1 w-6 h-6 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center" title={showSensitive[r.report_id] ? t("hide_evidence") : t("show_evidence")}>
              {showSensitive[r.report_id] ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="capitalize" style={{ backgroundColor: RISK_COLOR[r.risk_level] || "#9CA3AF", color: "#fff" }}>{t(r.risk_level) || r.risk_level}</Badge>
            <Badge variant="outline">{t(`cat_${r.category}`) || r.category}</Badge>
            <Badge variant="outline" className="capitalize">{t(`sub_${r.subcategory}`) || r.subcategory?.replace(/_/g," ")}</Badge>
            <Badge variant="outline" className="capitalize">{t(`st_${r.status}`) || r.status?.replace(/_/g," ")}</Badge>
            {r.contains_sensitive && <Badge className="bg-orange-100 text-orange-800 border-0">{t("sensitive_badge")}</Badge>}
          </div>
          <div className="font-semibold text-slate-900 mt-2 break-words">{r.description}</div>
          <div className="text-xs text-slate-500 mt-1">{t("author_line_by")} {displayAuthorName(r, t)} · {new Date(r.created_at).toLocaleString()} · {r.confirmations || 0} {t("confirmations_label")}</div>
          <ReviewReasonsBadges reasons={r.review_reasons} t={t} />
          {r.ai_analysis && <AIAnalysisPanel ai={r.ai_analysis} t={t} />}

          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
            <Select defaultValue={r.category} onValueChange={(v) => editReport(r.report_id, { category: v, subcategory: CATS[v][0] })}>
              <SelectTrigger data-testid={`edit-cat-${r.report_id}`} className="h-8 text-xs rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(CATS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select defaultValue={r.subcategory} onValueChange={(v) => editReport(r.report_id, { subcategory: v })}>
              <SelectTrigger data-testid={`edit-sub-${r.report_id}`} className="h-8 text-xs rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{(CATS[r.category] || []).map((s) => <SelectItem key={s} value={s} className="capitalize">{t(`sub_${s}`) || s.replace(/_/g," ")}</SelectItem>)}</SelectContent>
            </Select>
            <ChangeColorButton report={r} onSave={(risk, reason) => editReport(r.report_id, { risk_level: risk, reason })} t={t} />
            <ChangeStateButton report={r} onSave={(status, reason) => editReport(r.report_id, { status, reason })} t={t} />
          </div>

          <div className="mt-2 flex items-center gap-2">
            <Button size="sm" variant="outline" className="rounded-full h-8 text-xs hover:bg-orange-50 hover:text-orange-800"
              data-testid={`toggle-hidden-${r.report_id}`}
              onClick={() => editReport(r.report_id, { contains_sensitive: !r.contains_sensitive })}>
              {r.contains_sensitive ? t("make_public") : t("mark_sensitive")}
            </Button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {mod && (
              <>
                <Button data-testid={`approve-${r.report_id}`} onClick={() => decide(r.report_id, "published")} size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white"><CheckCircle2 size={14} className="mr-1" /> {t("approve")}</Button>
                <Button data-testid={`reject-${r.report_id}`} onClick={() => decide(r.report_id, "rejected")} size="sm" variant="outline" className="rounded-full hover:bg-red-50 hover:text-red-800"><XCircle size={14} className="mr-1" /> {t("reject")}</Button>
                <Button data-testid={`archive-${r.report_id}`} onClick={() => decide(r.report_id, "archived")} size="sm" variant="outline" className="rounded-full hover:bg-slate-900 hover:text-white">{t("archive")}</Button>
              </>
            )}
            {(r.status === "resolved" || r.status === "archived" || r.status === "rejected") && (
              <Button data-testid={`reopen-${r.report_id}`} onClick={() => reopenReport(r.report_id)} size="sm" variant="outline" className="rounded-full hover:bg-emerald-50 hover:text-emerald-800">
                <RotateCcw size={14} className="mr-1" /> {t("reopen")}
              </Button>
            )}
            {user.role === "admin" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid={`delete-open-${r.report_id}`} size="sm" variant="outline" className="rounded-full border-red-200 text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600"><Trash2 size={14} className="mr-1" /> {t("delete")}</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>{t("delete_report_title")}</AlertDialogTitle><AlertDialogDescription>{t("delete_report_desc")}</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`delete-cancel-${r.report_id}`}>{t("cancel")}</AlertDialogCancel>
                    <AlertDialogAction data-testid={`delete-confirm-${r.report_id}`} onClick={() => deleteReport(r.report_id)} className="bg-red-600 hover:bg-red-700 text-white">{t("delete")}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Link to={`/report/${r.report_id}`} className="text-xs text-emerald-700 hover:underline self-center ml-2">{t("open_details") || "Apri"}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function AIAnalysisPanel({ ai, t }) {
  if (!ai || typeof ai !== "object") return null;
  const conf = typeof ai.confidence === "number" ? Math.round(ai.confidence * 100) : null;
  const trust = typeof ai.trustworthiness === "number" ? Math.round(ai.trustworthiness * 100) : null;
  const coh = typeof ai.photo_text_coherence === "number" ? Math.round(ai.photo_text_coherence * 100) : null;
  const Row = ({ label, value, testid }) => value == null || value === "" ? null : (
    <div className="min-w-0 flex flex-col gap-0.5" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest text-slate-500 leading-none">{label}</span>
      <span className="text-xs text-slate-800 font-semibold break-words leading-snug">{value}</span>
    </div>
  );
  return (
    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 sm:p-4 space-y-3" data-testid="ai-analysis-panel">
      <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {t("ai_panel_title") || "AI moderation"}</div>
      {ai.problem_recognized && (
        <div className="text-sm text-slate-900 leading-snug break-words" data-testid="ai-problem">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 block mb-0.5">{t("ai_problem") || "Problema"}</span>
          {ai.problem_recognized}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
        <Row testid="ai-category" label={t("category")} value={ai.category} />
        <Row testid="ai-subcategory" label={t("subcategory")} value={ai.subcategory ? (t(`sub_${ai.subcategory}`) || ai.subcategory.replace(/_/g," ")) : null} />
        <Row testid="ai-suggested-color" label={t("suggested_color")} value={ai.suggested_color ? (t(ai.suggested_color) || ai.suggested_color) : null} />
        <Row testid="ai-severity" label={t("severity")} value={ai.severity ? (t(ai.severity) || ai.severity) : null} />
        <Row testid="ai-urgency" label={t("urgency")} value={ai.urgency ? (t(`urg_${ai.urgency}`) || ai.urgency) : null} />
        <Row testid="ai-confidence" label={t("confidence")} value={conf != null ? `${conf}%` : null} />
        <Row testid="ai-trust" label={t("trust")} value={trust != null ? `${trust}%` : null} />
        <Row testid="ai-coherence" label={t("coherence")} value={coh != null ? `${coh}%` : null} />
      </div>
      {(ai.sensitive_content || ai.legal_issues || ai.spam_or_irrelevant) && (
        <div className="flex flex-wrap gap-1.5">
          {ai.sensitive_content && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 font-semibold uppercase tracking-widest">{t("sensitive_badge")}</span>}
          {ai.legal_issues && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold uppercase tracking-widest">{t("legal_badge") || "legale"}</span>}
          {ai.spam_or_irrelevant && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-800 font-semibold uppercase tracking-widest">spam</span>}
        </div>
      )}
      {ai.severity_reason && (
        <div className="text-xs text-slate-700 leading-relaxed border-t border-emerald-200/60 pt-2 break-words" data-testid="ai-reason">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 block mb-0.5">{t("ai_reason") || "Motivazione"}</span>
          {ai.severity_reason}
        </div>
      )}
    </div>
  );
}

function ChangeStateButton({ report, onSave, t }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(report.status);
  const [reason, setReason] = useState("");
  const save = () => { onSave(status, reason); setOpen(false); setReason(""); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`change-state-${report.report_id}`} size="sm" variant="outline" className="rounded-full h-8 text-xs hover:bg-slate-900 hover:text-white">{t("state_arrow")}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("change_state_title")}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">{t("current_status")} <b className="text-slate-900">{report.status}</b></div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid={`state-select-${report.report_id}`} className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{t(s.labelKey)}</SelectItem>)}</SelectContent>
        </Select>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">{t("reason_optional")}</div>
          <Textarea data-testid={`state-reason-${report.report_id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("state_reason_placeholder")} className="rounded-xl" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full hover:bg-slate-100">{t("cancel")}</Button>
          <Button data-testid={`state-save-${report.report_id}`} onClick={save} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeColorButton({ report, onSave, t }) {
  const [open, setOpen] = useState(false);
  const [risk, setRisk] = useState(report.risk_level);
  const [reason, setReason] = useState("");
  const save = () => { onSave(risk, reason); setOpen(false); setReason(""); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`change-color-${report.report_id}`} size="sm" variant="outline" className="rounded-full h-8 text-xs hover:bg-slate-900 hover:text-white"><Palette size={12} className="mr-1" /> {t("color_short")}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t("change_color_title")}</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-500">{t("current_color")} <b className="uppercase" style={{ color: RISK_COLOR[report.risk_level] }}>{report.risk_level}</b></div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {["green","yellow","red"].map((c) => (
            <button key={c} type="button" data-testid={`color-option-${c}-${report.report_id}`} onClick={() => setRisk(c)} className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 ${risk === c ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}>
              <span className="w-6 h-6 rounded-full" style={{ background: RISK_COLOR[c] }} />
              <span className="text-xs font-semibold capitalize">{t(c) || c}</span>
            </button>
          ))}
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1.5">{t("reason_optional")}</div>
          <Textarea data-testid={`color-reason-${report.report_id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("color_reason_placeholder")} className="rounded-xl" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full hover:bg-slate-100">{t("cancel")}</Button>
          <Button data-testid={`color-save-${report.report_id}`} onClick={save} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("apply")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Lotto 13 — AdminUsersSection stabile, definita fuori da AdminDashboard.
 *
 * Il precedente `const UsersSection = () => (...)` era nidificato nel
 * body di AdminDashboard: ogni keystroke → setUserSearch → parent re-render
 * → nuova identità funzione → React unmount+remount del subtree → input
 * di ricerca ricreato → focus perso e tastiera mobile chiusa dopo ogni
 * singola lettera.
 *
 * Estrarre il componente fuori dal parent lo rende un tipo React stabile.
 * La query è ora **stato locale** del componente: cambia solo l'input +
 * la lista, non tutto AdminDashboard. La tastiera mobile resta aperta.
 */
function AdminUsersSection({ users, t, onBack, setRole, flagUser }) {
  const [query, setQuery] = React.useState("");
  const filteredUsers = React.useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return users || [];
    return (users || []).filter((u) => {
      const fields = [
        u.name, u.username, u.first_name, u.last_name,
        (u.first_name || "") + " " + (u.last_name || ""),
        u.email, u.user_id,
      ].map((s) => (s || "").toString().toLowerCase());
      return fields.some((s) => s.includes(q));
    });
  }, [users, query]);

  return (
    <div data-testid="admin-users-section">
      <SectionHeader onBack={onBack} title={t("admin_tab_users")} count={filteredUsers.length} />
      <div className="bg-white border border-slate-200 rounded-2xl mt-4">
        <div className="p-4 border-b border-slate-100">
          <Input
            placeholder={t("search_by_name_email") || "Cerca per nome, cognome, username o email…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="search"
            inputMode="search"
            className="h-10 rounded-full text-base"
            data-testid="user-search"
          />
          <div className="mt-2 text-[11px] text-slate-500" data-testid="user-search-count">
            {filteredUsers.length}/{(users || []).length} {t("users_shown") || "utenti mostrati"}
          </div>
        </div>
        {filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-slate-500" data-testid="user-search-empty">{t("no_results") || "Nessun risultato"}</div>
        ) : filteredUsers.map((u, i) => (
          <div key={u.user_id} className={`p-4 flex flex-col md:flex-row md:items-center gap-3 ${i > 0 ? "border-t border-slate-100" : ""}`} data-testid={`user-row-${u.user_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                {u.name}
                {u.suspended && <Badge className="bg-yellow-100 text-yellow-800 border-0">{t("admin_suspended")}</Badge>}
                {u.banned && <Badge className="bg-red-100 text-red-800 border-0">{t("admin_banned")}</Badge>}
              </div>
              <div className="text-xs text-slate-500 break-all">{u.email} · {t("admin_rep_short")} {u.reputation || 0}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select defaultValue={u.role} onValueChange={(v) => setRole(u.user_id, v)}>
                <SelectTrigger className="w-36 h-9 text-sm rounded-lg" data-testid={`role-select-${u.user_id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["citizen","volunteer","association","company","municipality","operator","moderator","admin"].map((r) => (
                    <SelectItem key={r} value={r}>{t(r) || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid={`suspend-${u.user_id}`} size="sm" variant="outline" className="rounded-full hover:bg-yellow-50 hover:text-yellow-800"
                onClick={() => flagUser(u.user_id, { suspended: !u.suspended })}>
                {u.suspended ? <><PlayCircle size={14} className="mr-1" /> {t("admin_reactivate")}</> : <><PauseCircle size={14} className="mr-1" /> {t("admin_suspend")}</>}
              </Button>
              <Button data-testid={`ban-${u.user_id}`} size="sm" variant="outline" className="rounded-full hover:bg-red-600 hover:text-white hover:border-red-600"
                onClick={() => flagUser(u.user_id, { banned: !u.banned })}>
                <Ban size={14} className="mr-1" /> {u.banned ? t("admin_remove_ban") : t("admin_ban")}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

